/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
    Box,
    Checkbox,
    Tooltip,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Popover,
    TextField,
    Button,
    Select,
    FormControl,
    InputLabel,
    Stack,
    Typography,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useSelector, dispatch } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { captureChecklistTransition, checklistTreatmentId } from 'src/utils/attendanceActions.ts';
import { invalidateAttendance } from 'src/redux/slices/attendanceReview.ts';
import assertApiOk from 'src/utils/assertApiOk';
import { getChecklistItemCompletionState } from 'src/utils/checklistViewModel';
import { readChecklistItemValue } from 'src/utils/checklistItemValue';
import Iconify from 'src/components/Iconify';
import { updateError } from 'src/redux/slices/user';
import { leadActions, followUpSlice } from 'src/redux/slices/lead';
import GoogleReviewSendDialog from 'src/components/dialogPopUp/GoogleReviewSendDialog';
import useGoogleReviewSend from 'src/hooks/useGoogleReviewSend';
import useAuth from 'src/hooks/useAuth';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import PaymentManualAcknowledgeDialog from 'src/sections/tasks/PaymentManualAcknowledgeDialog';
import SendFormsDialog from 'src/components/dialogPopUp/SendFormsDialog';
import { STATUS_CONFIG as CONSENT_STATUS_CONFIG } from 'src/sections/@dashboard/leadDetail/FormRequestCard';
import AddReminderPopup from 'src/components/dialogPopUp/AddReminderPopup';
import { createTreatmentPayment } from 'src/redux/slices/deposit';
import { getCharAtName } from 'src/utils/getCharAtName';
import { generateLabel } from 'src/utils/getReferralData';
import FilterAssigneeList from 'src/utils/filterAssigneeList';
import { AssignUserAvatar } from 'src/components/custom-avatar';
import IosEmailComposer from 'src/sections/tasks/composers/IosEmailComposer';
import IosWhatsAppComposer from 'src/sections/tasks/composers/IosWhatsAppComposerDialog';
import useDeliveryStatus, { _activityCache } from 'src/sections/tasks/useDeliveryStatus';
import useLegacyFollowUpFlow from 'src/sections/tasks/_shared/useLegacyFollowUpFlow';
import {
    armBookAppointmentCompletionWatch,
    settleBookAppointmentDialog,
} from 'src/sections/tasks/_shared/bookAppointmentCompletionWatch';
import { resolveLeadStatus, getStatusPrimaryLabel, PRIMARY_ACTION_STYLE } from 'src/sections/tasks/statusPrimaryAction';
import { broadcastTaskCompleting, TASK_EXIT_MS } from 'src/sections/tasks/_shared/taskExitAnimation';
import useFormsFlags from 'src/hooks/useFormsFlags';
import { error as errorPalette, warning, success, grey } from 'src/theme/palette';
import { useResponsive } from '../../../../hooks';
import { tableCellInnerBoxStyle, paymentChipStyle, paymentChipTypeStyle } from '../../../../styles/NewLeadTableRowStyle';
import { CHECKLIST_ITEM_TYPE, CHECKLIST_COMPLETION_STATE, INSTANTIATION_MODE, TASKS_V2_CHANGED_EVENT } from '../../../../constants/tasksV2';

const { YES, PAUSED, SKIPPED, PENDING } = CHECKLIST_COMPLETION_STATE;

// -----------------------------------------------
// notifyTaskCompletingIfDone
//
// After a genuine YES completion (postState success), check whether the
// parent task is now fully done. If so, fire the broadcastTaskCompleting
// event so every mounted V2 list view (TaskBoardView, LeadTaskList, etc.)
// plays the card exit animation and the Undo snackbar can appear.
//
// Uses a lightweight callApi GET instead of dispatch(getTask()) to avoid
// overwriting the global currentTask Redux slot (which the task slideout
// may be using concurrently).  No-op if v2 tasks are not enabled for this
// practice or if the task is not yet fully completed.
// -----------------------------------------------
function notifyTaskCompletingIfDone(practiceId, taskId, lastItemId) {
    if (!practiceId || !taskId) return;
    callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`)
        .then((res) => {
            if (res?.data?.status === 'completed') {
                broadcastTaskCompleting(String(taskId), TASK_EXIT_MS, {
                    taskSnapshot: res.data,
                    lastItemId: lastItemId || null,
                });
            }
        })
        .catch(() => {});
}

// Context that carries the onBeforeReopen gate down to ChecklistCell without
// prop-drilling through every intermediate cell wrapper.
const BeforeReopenContext = createContext(null);

// macOS system font stack — used for dialog chrome / pill labels
const MAC_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

// Item types that have a dedicated action composer (email, WhatsApp, note, etc.)
// A PENDING cell of one of these types triggers onActionClick (opens the popup)
// instead of jumping straight to the StatusMenu.
const ACTION_BEARING_TYPES = new Set([
    CHECKLIST_ITEM_TYPE.EMAIL_SENT,
    CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
    CHECKLIST_ITEM_TYPE.SMS_SENT,
    CHECKLIST_ITEM_TYPE.PHONE_CALLED,
    CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL,
    CHECKLIST_ITEM_TYPE.ADD_NOTE,
    CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM,
    CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST,
    CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK,
    CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER,
    CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT,
    CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE,
    CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND,
    CHECKLIST_ITEM_TYPE.START_TREATMENT,
    CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT,
    CHECKLIST_ITEM_TYPE.LEAD_STATUS_CHANGED,
    CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN,
    CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT,
    // Interactive input types — primary action opens input UI
    CHECKLIST_ITEM_TYPE.FREE_TEXT,
    CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE,
    CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION,
    CHECKLIST_ITEM_TYPE.CONFIRMATION,
    CHECKLIST_ITEM_TYPE.PAYMENT_METHOD,
    CHECKLIST_ITEM_TYPE.DATE_FIELD,
    CHECKLIST_ITEM_TYPE.SIMPLE_DATE,
    CHECKLIST_ITEM_TYPE.STAFF_PICKER,
]);

// Label and icon for the primary action shown at the top of the cell menu.
// Keyed by CHECKLIST_ITEM_TYPE; used by StatusMenu when onPrimaryAction is supplied.
const PRIMARY_ACTION_LABELS = {
    [CHECKLIST_ITEM_TYPE.STAFF_PICKER]:            'Select staff member',
    [CHECKLIST_ITEM_TYPE.EMAIL_SENT]:              'Send email',
    [CHECKLIST_ITEM_TYPE.WHATSAPP_SENT]:           'Send WhatsApp',
    [CHECKLIST_ITEM_TYPE.SMS_SENT]:                'Send SMS',
    [CHECKLIST_ITEM_TYPE.PHONE_CALLED]:            'Log phone call',
    [CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL]:          'Log phone call',
    [CHECKLIST_ITEM_TYPE.ADD_NOTE]:                'Add note',
    [CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM]:       'Send consent form',
    [CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST]:     'Send review request',
    [CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK]:       'Send payment link',
    [CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER]:       'Schedule reminder',
    [CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT]:        'Book appointment',
    [CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE]:       'Record attendance',
    [CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND]: 'Record FTA',
    [CHECKLIST_ITEM_TYPE.START_TREATMENT]:         'Start treatment',
    [CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT]:      'Complete treatment',
    [CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN]:     'Send treatment plan',
    [CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT]:         'Upload document',
    [CHECKLIST_ITEM_TYPE.FREE_TEXT]:               'Add note',
    [CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE]:         'Choose…',
    [CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION]:        'Next action',
    [CHECKLIST_ITEM_TYPE.CONFIRMATION]:            'Confirm',
    [CHECKLIST_ITEM_TYPE.DATE_FIELD]:              'Schedule reminder',
    [CHECKLIST_ITEM_TYPE.SIMPLE_DATE]:             'Set Date',
};

const PRIMARY_ACTION_ICONS = {
    [CHECKLIST_ITEM_TYPE.STAFF_PICKER]:            'eva:person-outline',
    [CHECKLIST_ITEM_TYPE.EMAIL_SENT]:              'eva:email-outline',
    [CHECKLIST_ITEM_TYPE.WHATSAPP_SENT]:           'ri:whatsapp-fill',
    [CHECKLIST_ITEM_TYPE.SMS_SENT]:                'eva:message-square-outline',
    [CHECKLIST_ITEM_TYPE.PHONE_CALLED]:            'eva:phone-call-outline',
    [CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL]:          'eva:phone-call-outline',
    [CHECKLIST_ITEM_TYPE.ADD_NOTE]:                'eva:file-text-outline',
    [CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM]:       'eva:file-text-outline',
    [CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST]:     'eva:star-outline',
    [CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK]:       'eva:credit-card-outline',
    [CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER]:       'eva:bell-outline',
    [CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT]:        'eva:calendar-outline',
    [CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE]:       'akar-icons:check',
    [CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND]: 'ci:close-big',
    [CHECKLIST_ITEM_TYPE.START_TREATMENT]:         'eva:play-circle-outline',
    [CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT]:      'eva:checkmark-circle-outline',
    [CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN]:     'eva:file-text-outline',
    [CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT]:         'eva:upload-outline',
    [CHECKLIST_ITEM_TYPE.FREE_TEXT]:               'eva:edit-outline',
    [CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE]:         'eva:checkmark-square-outline',
    [CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION]:        'solar:bolt-bold-duotone',
    [CHECKLIST_ITEM_TYPE.CONFIRMATION]:            'eva:checkmark-circle-outline',
    [CHECKLIST_ITEM_TYPE.DATE_FIELD]:              'eva:calendar-outline',
    [CHECKLIST_ITEM_TYPE.SIMPLE_DATE]:             'eva:calendar-outline',
};

// -----------------------------------------------
// Date colour constants
// -----------------------------------------------

// -----------------------------------------------
// Helpers
// -----------------------------------------------

const formatDateShort = (iso) => {
    if (!iso) return '';
    const m = moment(iso);
    return m.isValid() ? m.format('DD/MM/YYYY') : '';
};

const getDateColorKey = (scheduledDate) => {
    if (!scheduledDate) return null;
    const today = moment().startOf('day');
    const d = moment(scheduledDate).startOf('day');
    if (d.isBefore(today)) return 'past';
    if (d.isSame(today)) return 'today';
    return 'future';
};


function getCellColor(state, scheduledDate, cellError) {
    if (cellError) return errorPalette.dark;
    if (state === YES) return success.dark;        // green — universal "completed" signal
    if (state === PAUSED) return warning.dark;     // amber
    if (state === SKIPPED) return 'text.disabled';
    // PENDING fallthrough — colour by urgency
    const bucket = getDateColorKey(scheduledDate);
    if (bucket === 'past') return errorPalette.dark; // red — kept; only alarming state
    if (bucket === 'today') return 'text.primary';   // neutral dark — was orange
    return 'text.disabled';                          // quiet grey — was blue
}

function getCellBgTint(state, scheduledDate) {
    if (state === YES) return alpha(success.main, 0.04);
    if (state === SKIPPED) return grey[200];
    if (state === PAUSED) return alpha(warning.main, 0.04);   // faint amber wash
    // PENDING
    const bucket = getDateColorKey(scheduledDate);
    if (bucket === 'past') return alpha(errorPalette.main, 0.04); // faint red wash
    if (bucket === 'today') return 'rgba(0, 0, 0, 0.03)';         // neutral grey wash
    return 'transparent';
}

function getCellIcon(state, actionIcon) {
    if (state === YES) return 'eva:checkmark-circle-2-fill';
    if (state === SKIPPED) return 'eva:slash-outline';
    return actionIcon;
}

function getCellTooltip(state, scheduledDate, cellData) {
    if (state === YES) {
        const name = cellData.completed_by_practice_user?.full_name || cellData.completed_by_full_name || cellData.completed_by || 'someone';
        const when = cellData.completed_at ? moment(cellData.completed_at).format('DD MMM HH:mm') : '';
        return `Done by ${name}${when ? ` on ${when}` : ''}`;
    }
    if (state === PAUSED) return cellData.paused_until ? `Paused until ${formatDateShort(cellData.paused_until)}` : 'Paused';
    if (state === SKIPPED) return 'Skipped';
    if (scheduledDate) return `Due ${formatDateShort(scheduledDate)}`;
    return '';
}

function formatCellDate(state, scheduledDate, completedAt, pausedUntil) {
    if (state === YES && completedAt) {
        const m = moment(completedAt);
        return m.isSame(moment(), 'day') ? m.format('HH:mm') : m.format('DD MMM');
    }
    if (state === PAUSED && pausedUntil) return `until ${moment(pausedUntil).format('DD MMM')}`;
    // SKIPPED shows the literal word (the render applies the strike-through styling).
    // Previously returned '' → the cell rendered blank (no date, no label), which read
    // as a lost/empty cell after a skip. The slash icon alone wasn't enough signal.
    if (state === SKIPPED) return 'Skipped';
    if (!scheduledDate) return '';
    const m = moment(scheduledDate);
    const today = moment().startOf('day');
    const days = m.clone().startOf('day').diff(today, 'days');
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    if (days < 0) return `${-days}d ago`;
    if (days < 7) return m.format('ddd');
    return m.format('DD MMM');
}

function hasInlineValue(itemType, value) {
    if (value == null || value === '') return false;
    const VALUE_BEARING = new Set([
        CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT,
        CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE,
        CHECKLIST_ITEM_TYPE.FREE_TEXT,
        CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE,
        CHECKLIST_ITEM_TYPE.PAYMENT_METHOD,
        CHECKLIST_ITEM_TYPE.STAFF_PICKER,
        CHECKLIST_ITEM_TYPE.DATE_FIELD,
        CHECKLIST_ITEM_TYPE.SIMPLE_DATE,
    ]);
    return VALUE_BEARING.has(itemType);
}

function formatCellValue(itemType, value) {
    if (itemType === CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT) {
        const n = Number(value);
        return Number.isFinite(n) ? `£${n}` : String(value);
    }
    if (itemType === CHECKLIST_ITEM_TYPE.DATE_FIELD || itemType === CHECKLIST_ITEM_TYPE.SIMPLE_DATE) return formatDateShort(value);
    return String(value);
}

// -----------------------------------------------
// useStatusApi — shared hook: POST completion_state to BE
// -----------------------------------------------
function useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const postState = async (completionState, responsePayload) => {
        const previousState = getChecklistItemCompletionState(cellData);
        // Guard against stale/synthetic cellData whose task_id or checklist_item_id
        // is missing (e.g. PendingActionCell instantiation lands but child item
        // lookup failed, or a stale row pre-instantiation). Without this guard
        // the URL becomes /tasks/undefined/checklist/undefined/<endpoint>/ which
        // 404s silently and the next refetch wipes the row. Mirrors the guard
        // in ChecklistItemRow.jsx:460-467.
        const taskId = cellData?.task_id;
        const itemId = cellData?.checklist_item_id;
        if (!taskId || taskId === 'undefined' || !itemId || itemId === 'undefined') {
            dispatch(updateError({
                success: false,
                message: 'This checklist item is not ready yet — refreshing.',
            }));
            if (onUpdate) onUpdate();
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const body = { completion_state: completionState };
            if (responsePayload) body.response_payload = responsePayload;
            const endpoint = completionState === PENDING ? 'reopen' : 'complete';
            const response = await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/${endpoint}/`,
                'POST',
                completionState === PENDING ? {} : body
            ));
            if (onUpdate) onUpdate();
            // Broadcast the checklist state change so page-level listeners
            // (ConsultantAttend, TreatmentStarted) can refresh their strip counts.
            // Mirrors the pattern in taskSlice.emitTasksV2Changed — table-cell mutations
            // don't go through taskSlice so we fire it here after a confirmed success.
            if (typeof window !== 'undefined') window.dispatchEvent(new Event(TASKS_V2_CHANGED_EVENT));
            if (completionState === YES && onChecklistComplete) {
                onChecklistComplete(previousState);
            }
            if (completionState === PENDING && onChecklistReopen) {
                onChecklistReopen(previousState);
            }
            // Surface a snackbar when the BE signals that this completion cascaded
            // into a lead-level FSM transition. Gated on the `lead_status_changed`
            // flag added by BE PR #5140 — fires for ~5% of completions instead of
            // every YES tick. Flows through the existing followUpTableMoved
            // channel → LiveNotificationHandler renders an iOS-style toast that
            // links to the lead detail so the user can confirm the new status.
            if (
                completionState === YES &&
                response?.ok &&
                response?.data?.lead_status_changed === true &&
                leadData
            ) {
                const leadId = leadData.lead_id || String(leadData.id ?? '').split('-')[0];
                const leadName = leadData.full_name
                    || `${leadData.first_name || ''} ${leadData.last_name || ''}`.trim()
                    || 'Lead';
                if (leadId) {
                    dispatch(leadActions.setFollowUpTableMoved({
                        leadId,
                        leadName,
                        newLabel: 'the next stage',
                        noteAdded: false,
                    }));
                }
            }
        } catch (err) {
            setError(true);
            setTimeout(() => setError(false), 3000);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, postState };
}

// -----------------------------------------------
// Delivery status helpers — email icon progression + WhatsApp tick UX
// -----------------------------------------------

// Maps delivery status key → { icon, color } for EMAIL_SENT cells.
// Gives staff instant visual feedback without opening the cell.
function formatEmailDeliveryIcon(deliveryStatus) {
    if (!deliveryStatus) return null; // fall through to default state icon (green tick)
    const map = {
        sent:      { icon: 'eva:paper-plane-fill',  color: 'text.disabled' },
        delivered: { icon: 'eva:email-fill',         color: 'success.light' },
        opened:    { icon: 'eva:email-open-fill',    color: 'success.main' },
        clicked:   { icon: 'eva:email-open-fill',    color: 'primary.main' },
    };
    return map[deliveryStatus.key] || map.sent;
}

// WhatsApp 1-tick / 2-tick / blue-2-tick — mirrors the native WA read-receipt UX.
function WhatsAppTicks({ statusKey }) {
    const isRead = statusKey === 'read';
    const isDouble = statusKey === 'delivered' || isRead;
    const color = isRead ? 'primary.main' : 'text.disabled';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Iconify icon="eva:checkmark-fill" width={11} sx={{ color }} />
            {isDouble && <Iconify icon="eva:checkmark-fill" width={11} sx={{ color, ml: -0.75 }} />}
        </Box>
    );
}
WhatsAppTicks.propTypes = { statusKey: PropTypes.string };

// StatusMenu — right-click / long-press backup affordance
// -----------------------------------------------
const MARK_SENT_TABLE_TYPES = new Set(['WHATSAPP_SENT', 'EMAIL_SENT', 'SMS_SENT']);
const STATUS_MENU_PAPER_PROPS = { sx: { minWidth: 172, borderRadius: 1.5 } };

function StatusMenu({ anchorEl = null, onClose, onSelect, currentState = PENDING, itemType = '', onMarkSentManually, primaryActionLabel, primaryActionIcon, onPrimaryAction, nextActions = null, onSelectNextAction = null, allowPause = true }) {
    const showMarkSent = MARK_SENT_TABLE_TYPES.has(itemType) && currentState === PENDING;

    // LEAD_NEXT_ACTION: render each available next action inline + Pause + Skip + Reopen.
    // Each action fires onSelectNextAction(key) which threads resolved_action through.
    // Mark Done is intentionally absent here — LEAD_NEXT_ACTION requires resolved_action
    // in the payload (BE enforces it) so only the per-action items can commit YES.
    if (itemType === CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION) {
        const hasNextActions = Array.isArray(nextActions) && nextActions.length > 0 && onSelectNextAction && currentState === PENDING;
        return (
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose} PaperProps={STATUS_MENU_PAPER_PROPS}>
                {hasNextActions && nextActions.map((a) => (
                    <MenuItem key={a.key} dense onClick={() => { onClose(); onSelectNextAction(a.key); }} sx={{ gap: 1 }}>
                        <Iconify icon={a.icon || 'eva:plus-circle-outline'} width={16} sx={{ color: a.color || 'primary.main' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
                            {a.label}
                        </Typography>
                    </MenuItem>
                ))}
                {hasNextActions && <Divider sx={{ my: 0.5 }} />}
                <MenuItem dense selected={currentState === PAUSED} onClick={() => { onSelect(PAUSED); onClose(); }} sx={{ gap: 1 }}>
                    <Iconify icon="eva:pause-circle-outline" width={16} sx={{ color: 'warning.main' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Pause</Typography>
                </MenuItem>
                <MenuItem dense selected={currentState === SKIPPED} onClick={() => { onSelect(SKIPPED); onClose(); }} sx={{ gap: 1 }}>
                    <Iconify icon="eva:slash-outline" width={16} sx={{ color: 'text.disabled' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Skip</Typography>
                </MenuItem>
                {currentState !== PENDING && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem dense onClick={() => { onSelect(PENDING); onClose(); }} sx={{ gap: 1 }}>
                            <Iconify icon="eva:undo-outline" width={16} sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Reopen</Typography>
                        </MenuItem>
                    </>
                )}
            </Menu>
        );
    }

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            PaperProps={STATUS_MENU_PAPER_PROPS}
        >
            {onPrimaryAction && (
                <>
                    <MenuItem dense onClick={() => { onClose(); onPrimaryAction(); }} sx={{ gap: 1 }}>
                        <Iconify icon={primaryActionIcon || 'eva:plus-circle-outline'} width={16} sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
                            {primaryActionLabel || 'Action'}
                        </Typography>
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                </>
            )}
            {/* Secondary actions (e.g. the demoted Send Email / WhatsApp composer)
                — each keeps its own colour/icon so channels stay distinguishable. */}
            {Array.isArray(nextActions) && nextActions.length > 0 && onSelectNextAction && currentState === PENDING && (
                <>
                    {nextActions.map((a) => (
                        <MenuItem key={a.key} dense onClick={() => { onClose(); onSelectNextAction(a.key); }} sx={{ gap: 1 }}>
                            <Iconify icon={a.icon || 'eva:plus-circle-outline'} width={16} sx={{ color: a.color || 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                {a.label}
                            </Typography>
                        </MenuItem>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                </>
            )}
            <MenuItem dense selected={currentState === YES} onClick={() => { onClose(); onSelect(YES); }} sx={{ gap: 1 }}>
                <Iconify icon="eva:checkmark-circle-2-fill" width={16} sx={{ color: 'success.dark' }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Mark Done</Typography>
            </MenuItem>
            {showMarkSent && (
                <MenuItem dense onClick={() => { onClose(); if (onMarkSentManually) onMarkSentManually(); }} sx={{ gap: 1 }}>
                    <Iconify icon="eva:edit-2-outline" width={16} sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Mark as sent manually</Typography>
                </MenuItem>
            )}
            {/* Pause needs a paused_until date, which only ChecklistCell's pause dialog
                collects (it POSTs /pause/, not /complete/). Consumers that wire this menu
                straight to a completion call must opt out — otherwise selecting Pause would
                POST completion_state=PAUSED with no date, leaving an item paused forever with
                nothing for the resume cron to act on. */}
            {allowPause && (
                <MenuItem dense selected={currentState === PAUSED} onClick={() => { onSelect(PAUSED); onClose(); }} sx={{ gap: 1 }}>
                    <Iconify icon="eva:pause-circle-outline" width={16} sx={{ color: 'warning.main' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Pause</Typography>
                </MenuItem>
            )}
            <MenuItem dense selected={currentState === SKIPPED} onClick={() => { onSelect(SKIPPED); onClose(); }} sx={{ gap: 1 }}>
                <Iconify icon="eva:slash-outline" width={16} sx={{ color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Skip</Typography>
            </MenuItem>
            {currentState !== PENDING && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem dense onClick={() => { onSelect(PENDING); onClose(); }} sx={{ gap: 1 }}>
                        <Iconify icon="eva:undo-outline" width={16} sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Reopen</Typography>
                    </MenuItem>
                </>
            )}
        </Menu>
    );
}
StatusMenu.propTypes = {
    anchorEl: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    currentState: PropTypes.string,
    itemType: PropTypes.string,
    onMarkSentManually: PropTypes.func,
    primaryActionLabel: PropTypes.string,
    primaryActionIcon: PropTypes.string,
    onPrimaryAction: PropTypes.func,
    nextActions: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        color: PropTypes.string,
    })),
    onSelectNextAction: PropTypes.func,
    // false → hide Pause (see the comment on that menu item: only ChecklistCell's pause
    // dialog can supply the required paused_until).
    allowPause: PropTypes.bool,
};

// -----------------------------------------------
// -----------------------------------------------
// PauseDurationDialog — iOS-style dialog for choosing pause duration
// -----------------------------------------------

const PAUSE_PRESETS = [
    { label: 'Tomorrow', getValue: () => moment().add(1, 'day').startOf('day').hour(9) },
    { label: 'Next Week', getValue: () => moment().add(1, 'week').startOf('isoWeek').hour(9) },
    { label: 'Next Month', getValue: () => moment().add(1, 'month').startOf('month').hour(9) },
];

function PauseDurationDialog({ open, onClose, onConfirm, loading }) {
    const [selected, setSelected] = useState(null); // moment | null
    const [customMode, setCustomMode] = useState(false);
    const now = moment();

    const handlePreset = (preset) => {
        setCustomMode(false);
        setSelected(preset.getValue());
    };

    const handleCustom = () => {
        const opening = !customMode;
        setCustomMode(opening);
        if (opening && !selected) {
            setSelected(moment().add(1, 'day').startOf('day').hour(9));
        }
    };

    const handleConfirm = () => {
        if (!selected) return;
        onConfirm(selected.toISOString());
    };

    const isValid = selected && selected.isAfter(now);
    const resumeLabel = selected && isValid ? selected.format('DD MMM, HH:mm') : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: '14px', maxWidth: '420px' } }}
            TransitionProps={{ onExited: () => { setSelected(null); setCustomMode(false); } }}
        >
            <DialogTitle sx={{ pb: 1, fontFamily: MAC_FONT, fontWeight: 600, fontSize: '1.1rem', letterSpacing: '-0.01em', color: '#1D1D1F' }}>
                Pause until when?
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                {/* Horizontal 3-pill preset row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                    {PAUSE_PRESETS.map((p) => {
                        const val = p.getValue();
                        const isActive = selected && !customMode && selected.isSame(val, 'minute');
                        return (
                            <Box
                                key={p.label}
                                onClick={() => handlePreset(p)}
                                sx={{
                                    p: 1.25,
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    border: '1px solid',
                                    borderColor: isActive ? '#007AFF' : 'rgba(0, 0, 0, 0.08)',
                                    bgcolor: isActive ? '#007AFF' : '#FFFFFF',
                                    color: isActive ? '#FFFFFF' : '#1D1D1F',
                                    transition: 'all 150ms ease',
                                    '&:hover': isActive ? {} : { borderColor: 'rgba(0, 0, 0, 0.16)', bgcolor: 'rgba(0, 0, 0, 0.02)' },
                                }}
                            >
                                <Typography sx={{ fontFamily: MAC_FONT, fontSize: '13px', fontWeight: 600 }}>{p.label}</Typography>
                                <Typography sx={{ fontFamily: MAC_FONT, fontSize: '11px', opacity: isActive ? 0.85 : 0.65, mt: 0.25 }}>
                                    {val.format('ddd DD MMM')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Custom date & time inline toggle */}
                <Box
                    onClick={handleCustom}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                        mt: 1.5, pl: 0.5, color: customMode ? '#007AFF' : 'text.secondary',
                        fontSize: '12px', fontWeight: 500, fontFamily: MAC_FONT,
                        transition: 'color 150ms ease',
                        '&:hover': { color: '#007AFF' },
                    }}
                >
                    <Iconify icon={customMode ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={14} />
                    Custom date &amp; time
                </Box>

                <Collapse in={customMode} timeout={200}>
                    <Box sx={{ mt: 1.5 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateTimePicker
                                label="Resume at"
                                value={selected}
                                onChange={(val) => setSelected(val)}
                                minDateTime={now}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                </Collapse>

                {/* Muted resume summary — replaces Alert block */}
                {isValid && (
                    <Typography sx={{ mt: 2, fontSize: '12px', color: 'text.secondary', textAlign: 'left', fontFamily: MAC_FONT }}>
                        Resumes at{' '}
                        <Box component="span" sx={{ color: '#1D1D1F', fontWeight: 500 }}>{resumeLabel}</Box>
                        {' '}· also pauses other open tasks
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1, justifyContent: 'flex-end' }}>
                <Button
                    onClick={onClose}
                    sx={{
                        fontFamily: MAC_FONT, fontSize: '13px', fontWeight: 500,
                        textTransform: 'none', color: 'text.secondary',
                        bgcolor: 'transparent', boxShadow: 'none',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)', color: '#1D1D1F' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!isValid || loading}
                    sx={{
                        fontFamily: MAC_FONT, fontSize: '13px', fontWeight: 500,
                        textTransform: 'none', borderRadius: '8px',
                        bgcolor: '#007AFF', color: '#FFFFFF', boxShadow: 'none',
                        px: 2,
                        '&:hover': { bgcolor: '#0066D6', boxShadow: 'none' },
                        '&.Mui-disabled': { bgcolor: 'rgba(0, 122, 255, 0.4)', color: '#FFFFFF' },
                    }}
                >
                    {loading ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : 'Pause'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
PauseDurationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

// -----------------------------------------------
// ChecklistCell — unified cell renderer for all item types
// Layout: [icon] [date label]
// actionIcon  — per-type icon passed by type-specific wrappers (envelope, phone, WhatsApp, etc.)
//               Renders for all non-YES states; switches to green checkmark-circle when done.
function ChecklistCell({ cellData, practiceId, leadData, onUpdate, actionIcon, actionIconColor, onActionClick, deliveryStatus, nextActions, onSelectNextAction, consentStatusCfg, onChecklistComplete, onChecklistReopen, primaryActionLabelOverride, primaryActionIconOverride }) {
    const onBeforeReopen = useContext(BeforeReopenContext);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
    const [pauseLoading, setPauseLoading] = useState(false);
    const [markSentLoading, setMarkSentLoading] = useState(false);
    // Optimistic state — set immediately on user action so the cell visually
    // reflects the chosen state without waiting for the BE round-trip + table
    // refetch (which can be debounced up to ~200ms when multiple cells are
    // clicked rapidly). Cleared once the refreshed cellData reflects the
    // chosen state, or 2.5s as a safety timeout if the BE never agrees.
    const [optimisticState, setOptimisticState] = useState(null);
    const { error, postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const { enqueueSnackbar } = useCustomSnackbar();
    const actualState = getChecklistItemCompletionState(cellData);
    const state = optimisticState || actualState;
    useEffect(() => {
        if (optimisticState && actualState === optimisticState) setOptimisticState(null);
    }, [actualState, optimisticState]);
    useEffect(() => {
        if (!optimisticState) return undefined;
        const t = setTimeout(() => setOptimisticState(null), 2500);
        return () => clearTimeout(t);
    }, [optimisticState]);
    const scheduledDate = cellData.scheduled_date || null;
    const isArchived = leadData?.is_open_for_communication === false;
    const itemType = cellData.item_type || CHECKLIST_ITEM_TYPE.CONFIRMATION;

    const handleMarkSentManually = async () => {
        if (markSentLoading || isArchived) return;
        const taskId = cellData.task_id;
        const itemId = cellData.checklist_item_id;
        if (!taskId || !itemId || !practiceId) return;
        setMarkSentLoading(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/mark-sent-manually/`,
                'POST',
                { note: '' }
            ), 'Mark-sent failed');
            if (onUpdate) onUpdate();
        } catch {
            dispatch(updateError({ success: false, message: 'Could not mark as sent.' }));
        } finally {
            setMarkSentLoading(false);
        }
    };

    const handleStateChange = async (newState) => {
        if (isArchived) return;
        if (newState === PAUSED) {
            setPauseDialogOpen(true);
            return;
        }
        // Gate the reopen PATCH behind a confirmation dialog when the lead would
        // transition complete→in_progress. The gate resolves immediately (true)
        // for mid-checklist edits where no FSM transition occurs.
        if (newState === PENDING && onBeforeReopen) {
            const confirmed = await onBeforeReopen();
            if (!confirmed) return;
        }
        setOptimisticState(newState);
        // onChecklistComplete / onChecklistReopen are already called inside postState
        // on success — calling them again here would double-fire (double Undo animation).
        postState(newState).catch(() => { setOptimisticState(null); });
    };

    const handlePauseConfirm = async (pausedUntilIso) => {
        setPauseLoading(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${cellData.task_id}/checklist/${cellData.checklist_item_id}/pause/`,
                'POST',
                { paused_until: pausedUntilIso }
            ), 'Pause failed');
            setPauseDialogOpen(false);
            enqueueSnackbar('Task paused', { variant: 'success' });
            if (onUpdate) onUpdate();
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to pause — please try again.' }));
        } finally {
            setPauseLoading(false);
        }
    };

    const handleCellClick = (e) => {
        e.stopPropagation();
        if (isArchived) return;
        setMenuAnchor(e.currentTarget);
    };

    // Primary-action props for StatusMenu: only populated for action-bearing PENDING cells.
    // primaryActionLabelOverride/IconOverride let a wrapper (the email/WhatsApp
    // ComposerCell) replace the per-type label/icon with the unified "Follow Up"
    // primary so the primary is visually identical across every cell type.
    const showPrimaryAction = state === PENDING && ACTION_BEARING_TYPES.has(itemType) && Boolean(onActionClick);
    const primaryActionLabel = showPrimaryAction
        ? (primaryActionLabelOverride || PRIMARY_ACTION_LABELS[itemType] || cellData.title || 'Action')
        : undefined;
    const primaryActionIcon  = showPrimaryAction
        ? (primaryActionIconOverride || PRIMARY_ACTION_ICONS[itemType] || 'eva:plus-circle-outline')
        : undefined;
    const handlePrimaryAction = showPrimaryAction
        ? () => onActionClick((payload) => {
            setOptimisticState(YES);
            return postState(YES, payload).catch((err) => { setOptimisticState(null); throw err; });
        })
        : undefined;

    const cellColor = getCellColor(state, scheduledDate, error);
    const dateLabel = formatCellDate(state, scheduledDate, cellData.completed_at, cellData.paused_until);

    // Delivery-aware icon: EMAIL_SENT cells override the green tick with a
    // progression (paper-plane → email-fill → email-open-fill) so staff can
    // tell at a glance whether the email was delivered / opened / clicked.
    const isEmailCell = itemType === CHECKLIST_ITEM_TYPE.EMAIL_SENT;
    const isWaCell = itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT;
    const isConsentCell = itemType === CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM;
    let stateIcon = getCellIcon(state, actionIcon);
    // actionIconColor lets a wrapper override the completed-state icon colour
    // (e.g. YesNoToggleCell shows a RED icon for a "No" answer instead of the
    // default green completed tint). Email/consent overrides below still win
    // for their own cell types.
    let iconColor = actionIconColor || cellColor;
    if (isEmailCell && state === YES) {
        const emailIconCfg = formatEmailDeliveryIcon(deliveryStatus);
        if (emailIconCfg) {
            stateIcon = emailIconCfg.icon;
            iconColor = emailIconCfg.color;
        }
        // null → keep default green tick (no delivery data yet, or poll window closed)
    }
    // Consent status override: replace the generic green tick with the status
    // icon + color from FormRequestCard.STATUS_CONFIG when status data is available.
    // Falls back to generic green tick when consentStatusCfg is null (no ID stored,
    // or status fetch not yet complete).
    if (isConsentCell && state === YES && consentStatusCfg) {
        stateIcon = consentStatusCfg.icon;
        iconColor = consentStatusCfg.color;
    }

    // Saved-value line for the value-bearing types (see the render below). Explicit
    // `_display_label` from the routing cell wins; otherwise read response_payload.
    const storedValue = readChecklistItemValue(itemType, cellData);
    const inlineValueLabel = cellData._display_label
        || (hasInlineValue(itemType, storedValue) ? formatCellValue(itemType, storedValue) : '');

    let tooltipTitle = getCellTooltip(state, scheduledDate, cellData);
    if (deliveryStatus && (isEmailCell || isWaCell)) {
        const labels = { sent: 'Sent', delivered: 'Delivered', opened: 'Opened', clicked: 'Clicked', read: 'Read' };
        tooltipTitle = labels[deliveryStatus.key] || tooltipTitle;
    }
    if (isConsentCell && state === YES && consentStatusCfg) {
        tooltipTitle = consentStatusCfg.label;
    }
    if (isArchived) tooltipTitle = 'Lead is archived';
    else if (error) tooltipTitle = 'Failed to update — try again';

    return (
        <>
            <Tooltip title={tooltipTitle}>
                <Box
                    onClick={handleCellClick}
                    onContextMenu={(e) => { e.preventDefault(); if (!isArchived && !(itemType === CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION && state === YES)) setMenuAnchor(e.currentTarget); }}
                    sx={{
                        ...tableCellInnerBoxStyle,
                        bgcolor: getCellBgTint(state, scheduledDate),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        p: '6px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 150ms ease',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                >
                    {/* Type icon (or delivery-aware icon for EMAIL/WA) left, date right-aligned */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                        <Iconify
                            icon={stateIcon}
                            width={16}
                            sx={{ color: iconColor, opacity: state === SKIPPED ? 0.5 : 1, flexShrink: 0 }}
                        />
                        {dateLabel && (
                            <Typography
                                sx={{
                                    fontFamily: MAC_FONT,
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    color: cellColor,
                                    whiteSpace: 'nowrap',
                                    textDecoration: state === SKIPPED ? 'line-through' : 'none',
                                }}
                            >
                                {dateLabel}
                            </Typography>
                        )}
                    </Box>
                    {/* WhatsApp tick indicator — 1 grey tick = sent, 2 grey = delivered, 2 blue = read.
                        Default to 'sent' when deliveryStatus is null (poll closed or no activity yet). */}
                    {isWaCell && state === YES && (
                        <WhatsAppTicks statusKey={deliveryStatus?.key || 'sent'} />
                    )}
                    {/* Consent status label — shown below the icon when status data is available.
                        Falls back silently (no label) until the status fetch completes. */}
                    {isConsentCell && state === YES && consentStatusCfg && (
                        <Typography sx={{ fontSize: '9px', fontWeight: 700, color: consentStatusCfg.color, whiteSpace: 'nowrap', lineHeight: 1 }}>
                            {consentStatusCfg.label}
                        </Typography>
                    )}
                    {/* Saved value line. Two bugs fixed here:
                        - `_display_label` was set by five cells (chosen option, free text,
                          Yes/No answer, uploaded filename) and read by NOTHING, so those
                          labels never rendered.
                        - `cellData.value` is always undefined: there is no `value` field on
                          a checklist item (see src/utils/checklistItemValue.js), so this line
                          never appeared for stored values either.
                        An explicit label from the cell wins; otherwise read the value out of
                        response_payload, where it actually lives. */}
                    {inlineValueLabel && (
                        <Typography sx={{ fontSize: '10px', color: 'text.secondary', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {inlineValueLabel}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
            <StatusMenu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onSelect={(s) => { setMenuAnchor(null); handleStateChange(s); }}
                currentState={state}
                itemType={itemType}
                onMarkSentManually={handleMarkSentManually}
                primaryActionLabel={primaryActionLabel}
                primaryActionIcon={primaryActionIcon}
                onPrimaryAction={handlePrimaryAction}
                nextActions={nextActions}
                onSelectNextAction={onSelectNextAction}
            />
            <PauseDurationDialog
                open={pauseDialogOpen}
                onClose={() => setPauseDialogOpen(false)}
                onConfirm={handlePauseConfirm}
                loading={pauseLoading}
            />
        </>
    );
}
ChecklistCell.propTypes = {
    cellData: PropTypes.object.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadData: PropTypes.object,
    onUpdate: PropTypes.func,
    // eslint-disable-next-line react/no-unused-prop-types
    actionIcon: PropTypes.string, // per-type icon (envelope, phone, etc.); switches to green tick when YES
    actionIconColor: PropTypes.string, // optional override for the completed icon colour (e.g. red for a "No" answer)
    onActionClick: PropTypes.func,
    deliveryStatus: PropTypes.shape({ key: PropTypes.string, priority: PropTypes.number }),
    // LEAD_NEXT_ACTION extras — array of available actions + handler to fire one by key
    nextActions: PropTypes.array,
    onSelectNextAction: PropTypes.func,
    // SEND_CONSENT_FORM: fetched consent status config ({ label, color, icon, bg })
    // from FormRequestCard.STATUS_CONFIG — null until status is fetched or if no consent_request_id stored.
    consentStatusCfg: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string, icon: PropTypes.string, bg: PropTypes.string }),
    onChecklistComplete: PropTypes.func,
    onChecklistReopen: PropTypes.func,
    // Unified-primary overrides — replace the per-type primary label/icon with the
    // shared "Follow Up" style (supplied by the email/WhatsApp ComposerCell).
    primaryActionLabelOverride: PropTypes.string,
    primaryActionIconOverride: PropTypes.string,
};
ChecklistCell.defaultProps = { deliveryStatus: null, nextActions: null, onSelectNextAction: null, consentStatusCfg: null, primaryActionLabelOverride: undefined, primaryActionIconOverride: undefined };

// -----------------------------------------------
// FreeTextPopover — inline popover for FREE_TEXT items
// -----------------------------------------------
function FreeTextPopover({ open, anchorEl, onClose, onSave, loading }) {
    const [text, setText] = useState('');
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 1.5, width: 260, borderRadius: 1.5 } }}
        >
            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>Add note</Typography>
            <TextField
                autoFocus
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                fullWidth
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your note…"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSave(text); } }}
                sx={{ mb: 1 }}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button size="small" onClick={onClose} color="inherit">Cancel</Button>
                <Button size="small" variant="contained" onClick={() => onSave(text)} disabled={!text.trim() || loading} startIcon={loading ? <CircularProgress size={12} /> : null}>
                    Save
                </Button>
            </Stack>
        </Popover>
    );
}
FreeTextPopover.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

// -----------------------------------------------
// MultipleChoicePopover — inline menu for MULTIPLE_CHOICE items
// Options may be plain strings (legacy) or {id, label} objects (post-migration).
// Always saves option IDs (or the string itself for legacy) to response_payload.choices.
// -----------------------------------------------
const _optId = (opt) => (typeof opt === 'object' ? opt.id : opt);
const _optLabel = (opt) => (typeof opt === 'object' ? opt.label : opt);

function MultipleChoicePopover({ open, anchorEl, onClose, options, onSave, loading }) {
    const [selected, setSelected] = useState(new Set());

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    if (options.length === 0) {
        return (
            <Menu anchorEl={anchorEl} open={open} onClose={onClose} PaperProps={{ sx: { minWidth: 220, borderRadius: 1.5 } }}>
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', display: 'block' }}>
                        No options configured for this column. Open template settings to add options.
                    </Typography>
                </Box>
            </Menu>
        );
    }

    return (
        <Menu anchorEl={anchorEl} open={open} onClose={onClose} PaperProps={{ sx: { minWidth: 180, borderRadius: 1.5 } }}>
            {options.map((opt) => {
                const id = _optId(opt);
                const label = _optLabel(opt);
                return (
                    <MenuItem key={id} dense onClick={() => toggle(id)} sx={{ gap: 1 }}>
                        <Checkbox size="small" checked={selected.has(id)} sx={{ p: 0 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{label}</Typography>
                    </MenuItem>
                );
            })}
            <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button size="small" fullWidth variant="contained" onClick={() => onSave(Array.from(selected))} disabled={selected.size === 0 || loading}>
                    Save
                </Button>
            </Box>
        </Menu>
    );
}
MultipleChoicePopover.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })])).isRequired,
    onSave: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

// -----------------------------------------------
// Type-specific wrappers — thin shells over ChecklistCell
// -----------------------------------------------

function ConfirmationCell({ cellData, colMeta, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const isMobile = useResponsive('down', 'sm');
    const requiresConfirmation = colMeta?.action_config?.requires_confirmation || cellData?.action_config?.requires_confirmation || false;
    const state = getChecklistItemCompletionState(cellData);
    const title = colMeta?.column_label || cellData?.column_label || 'Did you complete this?';

    const handleAnswer = async (answer) => {
        try {
            await postState(YES, { answer });
            setOpen(false);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to confirm — please try again.' }));
        }
    };

    if (requiresConfirmation && state === PENDING) {
        return (
            <>
                <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:checkmark-square-2-outline" onActionClick={() => setOpen(true)} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
                <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Confirm action</DialogTitle>
                    <DialogContent sx={{ pt: '8px !important' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {title}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => handleAnswer('no')} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>No</Button>
                        <Button onClick={() => handleAnswer('yes')} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>Yes, confirm</Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    // Completed — show saved answer label in cell's primary slot
    // Prefer new 'answer' key; fall back to legacy 'confirmation_answer' for
    // items completed before the write-path was updated.
    const confirmAnswer = cellData.response_payload?.confirmation_answer ?? cellData.response_payload?.answer;
    let displayLabel = null;
    if (confirmAnswer === 'yes') displayLabel = 'Confirmed';
    else if (confirmAnswer === 'no') displayLabel = 'Declined';

    return (
        <ChecklistCell
            cellData={{ ...cellData, _display_label: displayLabel || cellData._display_label }}
            practiceId={practiceId}
            leadData={leadData}
            onUpdate={onUpdate}
            onChecklistComplete={onChecklistComplete}
            onChecklistReopen={onChecklistReopen}
           
        />
    );
}
ConfirmationCell.propTypes = { cellData: PropTypes.object.isRequired, colMeta: PropTypes.object, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// YesNoToggleCell — instant two-button inline toggle (no modal)
// Payload: { answer: 'yes' | 'no' }
// Falls back to confirmation_answer for items saved via the old ConfirmationCell fallback path.
// -----------------------------------------------

function YesNoToggleCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [saving, setSaving] = useState(false);
    const state = getChecklistItemCompletionState(cellData);
    // Support both the new 'answer' key and the legacy 'confirmation_answer' key from the ConfirmationCell fallback path
    const savedAnswer = cellData.response_payload?.answer ?? cellData.response_payload?.confirmation_answer;
    const isArchived = leadData?.is_open_for_communication === false;

    const handleAnswer = async (answer) => {
        if (saving || isArchived) return;
        const previousState = getChecklistItemCompletionState(cellData);
        setSaving(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${cellData.task_id}/checklist/${cellData.checklist_item_id}/complete/`,
                'POST',
                { completion_state: YES, response_payload: { answer } }
            ));
            if (onUpdate) onUpdate();
            if (onChecklistComplete) onChecklistComplete(previousState);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to save — please try again.' }));
        } finally {
            setSaving(false);
        }
    };

    // Completed state — show saved answer via ChecklistCell label slot
    if (state !== PENDING) {
        // eslint-disable-next-line no-nested-ternary
        const label = savedAnswer === 'yes' ? 'Yes' : (savedAnswer === 'no' ? 'No' : (savedAnswer || '—'));
        const icon = savedAnswer === 'yes' ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-fill';
        // A "No" answer is still a completed item, but it should read as red, not
        // the green completed tint — so the column communicates the answer at a glance.
        const iconColor = savedAnswer === 'no' ? 'error.main' : undefined;
        return (
            <ChecklistCell
                cellData={{ ...cellData, _display_label: label }}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon={icon}
                actionIconColor={iconColor}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}

            />
        );
    }

    // Pending state — render inline Yes / No buttons directly (ChecklistCell has no renderCustomAction slot)
    return (
        <Box
            sx={{
                ...tableCellInnerBoxStyle,
                bgcolor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                px: 0.5,
                py: 0.25,
                height: '100%',
                width: '100%',
            }}
        >
            <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={saving || isArchived}
                onClick={() => handleAnswer('yes')}
                sx={{ minWidth: 46, px: 1.25, py: 0.25, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1.5, textTransform: 'none', lineHeight: 1.4 }}
            >
                Yes
            </Button>
            <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={saving || isArchived}
                onClick={() => handleAnswer('no')}
                sx={{ minWidth: 46, px: 1.25, py: 0.25, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1.5, textTransform: 'none', lineHeight: 1.4 }}
            >
                No
            </Button>
        </Box>
    );
}
YesNoToggleCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };


// -----------------------------------------------
// NoContactCell — EMAIL_SENT / WHATSAPP_SENT cell when lead has no contact info.
// Renders a muted "No email" / "No phone" caption and exposes Mark Done / Skip
// via the shared StatusMenu so admins can resolve the row without sending.
// Reuses useStatusApi → same PATCH endpoint as every other checklist cell.
// -----------------------------------------------
function NoContactCell({ cellData, practiceId, leadData, onUpdate, missingLabel, tooltip, onChecklistComplete, onChecklistReopen }) {
    const onBeforeReopen = useContext(BeforeReopenContext);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [optimisticState, setOptimisticState] = useState(null);
    const { error, postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const actualState = getChecklistItemCompletionState(cellData);
    const state = optimisticState || actualState;
    const isArchived = leadData?.is_open_for_communication === false;
    const itemType = cellData.item_type || CHECKLIST_ITEM_TYPE.CONFIRMATION;

    useEffect(() => {
        if (optimisticState && actualState === optimisticState) setOptimisticState(null);
    }, [actualState, optimisticState]);
    useEffect(() => {
        if (!optimisticState) return undefined;
        const t = setTimeout(() => setOptimisticState(null), 2500);
        return () => clearTimeout(t);
    }, [optimisticState]);

    const handleStateChange = async (newState) => {
        if (isArchived) return;
        if (newState === PENDING && onBeforeReopen) {
            const confirmed = await onBeforeReopen();
            if (!confirmed) return;
        }
        setOptimisticState(newState);
        postState(newState).catch(() => { setOptimisticState(null); });
    };

    const handleCellClick = (e) => {
        e.stopPropagation();
        if (isArchived) return;
        setMenuAnchor(e.currentTarget);
    };

    const cellColor = getCellColor(state, null, error);
    const showCaption = state === PENDING;
    const stateIcon = getCellIcon(state, itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT ? 'ri:whatsapp-fill' : 'eva:email-outline');
    let tooltipTitle = state === PENDING ? tooltip : getCellTooltip(state, null, cellData);
    if (isArchived) tooltipTitle = 'Lead is archived';
    else if (error) tooltipTitle = 'Failed to update — try again';

    return (
        <>
            <Tooltip title={tooltipTitle}>
                <Box
                    onClick={handleCellClick}
                    onContextMenu={(e) => { e.preventDefault(); if (!isArchived) setMenuAnchor(e.currentTarget); }}
                    sx={{
                        ...tableCellInnerBoxStyle,
                        bgcolor: getCellBgTint(state, null),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        p: '6px 8px',
                        cursor: isArchived ? 'default' : 'pointer',
                        transition: 'background-color 150ms ease',
                        '&:hover': { bgcolor: isArchived ? undefined : 'rgba(0, 0, 0, 0.04)' },
                    }}
                >
                    <Iconify
                        icon={stateIcon}
                        width={16}
                        sx={{ color: cellColor, opacity: state === SKIPPED ? 0.5 : 0.6, flexShrink: 0 }}
                    />
                    {showCaption && (
                        <Typography
                            sx={{
                                fontFamily: MAC_FONT,
                                fontSize: '10px',
                                fontWeight: 500,
                                color: 'text.disabled',
                                whiteSpace: 'nowrap',
                                fontStyle: 'italic',
                            }}
                        >
                            {missingLabel}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
            <StatusMenu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onSelect={(s) => { setMenuAnchor(null); handleStateChange(s); }}
                currentState={state}
                itemType={itemType}
            />
        </>
    );
}
NoContactCell.propTypes = {
    cellData: PropTypes.object.isRequired,
    practiceId: PropTypes.any,
    leadData: PropTypes.object,
    onUpdate: PropTypes.func,
    missingLabel: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    onChecklistComplete: PropTypes.func,
    onChecklistReopen: PropTypes.func,
};

// ETM resolve-on-open (shared by the table-cell composers). Given a checklist
// item's SOURCE template-item id, resolves the per-practice engagement template
// the practice should send, so the composer preloads it. Uses the lead's OWN
// practice id — never a caller-supplied prop (same security gate as the slideout
// composer in ChecklistItemRow). Fires only while the composer is open for an
// EMAIL item that has a source template-item id; falls back silently to null.
function useResolvedEmailTemplateId(composerOpen, itemType, templateItemId, leadData, practiceId) {
    const [resolved, setResolved] = useState(null);
    const resolvePracticeId = leadData?.practice || leadData?.practice_id || practiceId;
    useEffect(() => {
        if (!composerOpen || itemType !== CHECKLIST_ITEM_TYPE.EMAIL_SENT || !templateItemId || !resolvePracticeId) return undefined;
        let active = true;
        callApi(`/api/v1/practices/${resolvePracticeId}/checklist-email-mappings/resolve/?template_checklist_item=${templateItemId}`)
            .then((res) => {
                if (!active) return;
                const id = res?.data?.[0]?.engagement_template_id ?? null;
                if (id != null) setResolved(id);
            })
            .catch(() => { /* silent — fall back to action_config template or blank */ });
        return () => { active = false; };
    }, [composerOpen, itemType, templateItemId, resolvePracticeId]);
    useEffect(() => { if (!composerOpen) setResolved(null); }, [composerOpen]);
    return resolved;
}

function ComposerCell({ cellData, practiceId, leadData, onUpdate, icon, composerType, onChecklistComplete, onChecklistReopen, onSelectedRow }) {
    const [composerOpen, setComposerOpen] = useState(false);
    const [activeType, setActiveType] = useState(composerType);
    const [optimisticDelivery, setOptimisticDelivery] = useState(null);
    const markCompleteRef = useRef(null);

    // Wire delivery-status polling for EMAIL_SENT / WHATSAPP_SENT cells.
    // Shows icon progression / WA ticks as the BE activity feed updates.
    // Pass null itemType for SMS (routes to LeadTemplateDialog / DisabledCell — no delivery wiring needed).
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const itemType = cellData.item_type;
    const isEmailOrWa = itemType === CHECKLIST_ITEM_TYPE.EMAIL_SENT || itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT;
    const completionState = getChecklistItemCompletionState(cellData);
    const completedAt = cellData?.completed_at || null;
    const deliveryStatus = useDeliveryStatus(practiceId, leadId, isEmailOrWa ? itemType : null, completionState, completedAt, optimisticDelivery);

    // For WA cells: look up thread from whatsappV2 store (same as TaskReminder)
    const threadMessages = useSelector((state) => state.whatsappV2.threadMessages);
    const waThreadInfo = useMemo(() => {
        if (!Array.isArray(threadMessages) || !leadData?.id) return null;
        const numericLeadId = String(leadData?.lead_id ?? String(leadData?.id ?? '').split('-')[0]);
        const contact = threadMessages.find((c) => String(c?.lead?.id) === numericLeadId);
        if (!contact) return null;
        return { thread: contact, phoneNumber: contact?.to_waid || '' };
    }, [threadMessages, leadData?.id]);

    // ETM: per-practice email template to preload (resolved from the source
    // template item); falls back to a static action_config template, then blank.
    const resolvedEmailTemplateId = useResolvedEmailTemplateId(composerOpen, itemType, cellData.template_item_id, leadData, practiceId);
    const effectiveEmailTemplateId = resolvedEmailTemplateId ?? cellData.action_config?.template_id ?? null;

    const openComposer = useCallback(() => {
        setActiveType(composerType);
        setComposerOpen(true);
    }, [composerType]);

    // Unified primary (#unified-primary): the email/WhatsApp cell's PRIMARY is
    // now status-aware "Follow Up" — it opens the per-status popup (same routing
    // the LeadNextActionCell uses) instead of always the contact wizard. The
    // specific Email/WhatsApp composer is demoted to the dropdown (see
    // composerMenuActions below). Falls back to opening the composer directly
    // when no popup router (onSelectedRow) is wired.
    const composerLeadStatus = resolveLeadStatus(leadData);
    const composerHasPrimary = !!getStatusPrimaryLabel(composerLeadStatus) && !!onSelectedRow && !!leadId;

    const handleActionClick = (markComplete) => {
        markCompleteRef.current = markComplete;
        // task #24 (LOCAL-ONLY): when the table has a popup router, open the
        // guided popup instead of the standalone composer — same pattern as
        // LogPhoneCallCell.handleTrigger (line 1611).
        if (onSelectedRow && leadId) {
            const rowKey = resolveFollowUpRowKey(composerLeadStatus);
            dispatch(followUpSlice(leadId, practiceId, undefined, () => {
                onSelectedRow(leadData, rowKey, true, { v2TaskId: cellData?.task_id });
            }));
            return;
        }
        openComposer();
    };

    // Secondary dropdown entry: the specific composer (Send Email / WhatsApp),
    // keeping its own colour/icon so users can still tell channels apart. Only
    // surfaced when the unified Follow Up primary is in play (otherwise the
    // primary already IS the composer).
    const composerMenuActions = composerHasPrimary
        ? [{
            key: 'OPEN_COMPOSER',
            label: composerType === 'WHATSAPP' ? 'Send WhatsApp' : 'Send email',
            icon: composerType === 'WHATSAPP' ? 'ri:whatsapp-fill' : 'eva:email-outline',
            color: composerType === 'WHATSAPP' ? '#25D366' : '#078DEE',
        }]
        : null;
    const handleComposerMenuSelect = (key) => {
        if (key === 'OPEN_COMPOSER') openComposer();
    };

    const handleSend = (messageId) => {
        setComposerOpen(false);
        const mc = markCompleteRef.current;
        markCompleteRef.current = null;
        if (mc) mc({ channel: activeType.toLowerCase(), messageId: messageId || null });
    };

    const isWa = activeType === 'WHATSAPP';

    const email = leadData?.email || leadData?.lead_contact?.email || leadData?.originalData?.lead_contact?.email;
    const phone = leadData?.phone || leadData?.lead_contact?.phone || leadData?.originalData?.lead_contact?.phone;
    // No-contact state: render a muted "No email" / "No phone" cell that still
    // exposes Skip (and Mark Done) via the StatusMenu, so admins can resolve the
    // row without inventing a fake contact. Bug #147 — was previously falling
    // through to DisabledCell which has pointer-events:none and rendered blank.
    if (composerType === 'EMAIL' && !email) {
        return (
            <NoContactCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                missingLabel="No email"
                tooltip="Lead has no email address — add one to send, or skip this step"
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
        );
    }
    if (composerType === 'WHATSAPP' && !phone) {
        return (
            <NoContactCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                missingLabel="No phone"
                tooltip="Lead has no phone number — add one to send, or skip this step"
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
        );
    }

    // Compute 24h window state from thread's last inbound message.
    // No thread or no inbound message = expired (only templates allowed by WA Business API).
    const isWaWindowExpired = (() => {
        if (composerType !== 'WHATSAPP') return false;
        const contact = waThreadInfo?.thread;
        if (!contact?.last_message) return true;
        const lastMsg = contact.last_message;
        const isInbound = lastMsg.from_waid === contact.to_waid;
        if (!isInbound) return true;
        const ts = lastMsg.updated_at || lastMsg.created_at;
        if (!ts) return true;
        return Date.now() - new Date(ts).getTime() > 24 * 60 * 60 * 1000;
    })();

    return (
        <>
            <ChecklistCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon={icon}
                onActionClick={handleActionClick}
                deliveryStatus={deliveryStatus}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
                primaryActionLabelOverride={composerHasPrimary ? PRIMARY_ACTION_STYLE.label : undefined}
                primaryActionIconOverride={composerHasPrimary ? PRIMARY_ACTION_STYLE.icon : undefined}
                nextActions={composerMenuActions}
                onSelectNextAction={composerMenuActions ? handleComposerMenuSelect : undefined}
            />
            {/* WA path: IosWhatsAppComposer */}
            {composerOpen && isWa && leadData && (
                <IosWhatsAppComposer
                    open={composerOpen}
                    onClose={() => setComposerOpen(false)}
                    practiceId={practiceId}
                    leadId={(leadData?.lead_id || String(leadData?.id ?? '').split('-')[0])}
                    leadDetail={leadData}
                    phoneNumber={waThreadInfo?.phoneNumber || leadData?.phone || leadData?.lead_contact?.phone || ''}
                    windowExpired={isWaWindowExpired}
                    onSendSuccess={(threadId) => {
                        const cacheKey = `${practiceId}-${(leadData?.lead_id || String(leadData?.id ?? '').split('-')[0])}`;
                        delete _activityCache[cacheKey];
                        setOptimisticDelivery('sent');
                        handleSend(threadId);
                    }}
                />
            )}
            {/* Email path: IosEmailComposer with onSendSuccess */}
            {composerOpen && activeType === 'EMAIL' && leadData && (
                <IosEmailComposer
                    open={composerOpen}
                    onClose={() => setComposerOpen(false)}
                    practiceId={practiceId}
                    leadId={(leadData?.lead_id || String(leadData?.id ?? '').split('-')[0])}
                    leadDetail={leadData}
                    templateId={effectiveEmailTemplateId}
                    onSendSuccess={(messageId) => {
                        // Bust activity cache so delivery status refreshes on next render
                        const cacheKey = `${practiceId}-${(leadData?.lead_id || String(leadData?.id ?? '').split('-')[0])}`;
                        delete _activityCache[cacheKey];
                        setOptimisticDelivery('sent');
                        handleSend(messageId);
                    }}
                />
            )}
            {/* SMS path: keep LeadTemplateDialog */}
            {composerOpen && activeType === 'SMS' && leadData && (
                <LeadTemplateDialog
                    open={composerOpen}
                    onClose={() => setComposerOpen(false)}
                    onSend={handleSend}
                    practiceId={practiceId}
                    type={activeType}
                    leadDetail={leadData}
                    templateId={cellData.action_config?.template_id}
                />
            )}
        </>
    );
}
ComposerCell.propTypes = {
    cellData: PropTypes.object.isRequired,
    practiceId: PropTypes.any,
    leadData: PropTypes.object,
    onUpdate: PropTypes.func,
    icon: PropTypes.string.isRequired,
    composerType: PropTypes.string.isRequired,
    onChecklistComplete: PropTypes.func,
    onChecklistReopen: PropTypes.func,
    onSelectedRow: PropTypes.func,
};

// -----------------------------------------------
// SimpleTextDialog — shared dialog for LOG_PHONE_CALL and ADD_NOTE
// -----------------------------------------------
function SimpleTextDialog({ open, onClose, title, placeholder, onSave, loading }) {
    const [text, setText] = useState('');
    const isMobile = useResponsive('down', 'sm');
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>{title}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <TextField
                    autoFocus
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    placeholder={placeholder}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>Cancel</Button>
                <Button
                    onClick={() => { onSave(text.trim()); setText(''); }}
                    variant="contained"
                    disabled={!text.trim() || loading}
                    sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                >
                    {loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
SimpleTextDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

// -----------------------------------------------
// LOG_PHONE_CALL — opens note dialog, POSTs note with contacted_via: PHONE
// -----------------------------------------------
function LogPhoneCallCell({ cellData, practiceId, leadData, onUpdate, onRefresh, onSelectedRow, onChecklistComplete, onChecklistReopen }) {
    // Track whether a genuine phone-call note was logged before the popup closed.
    // Hooks must be declared before any early return (rules-of-hooks).
    const completionWatchRef = useRef(null);
    // Use the refresh-only callback for postState so it calls handleRefresh(false) in the
    // slideout context — ensuring notifyTaskCompletingIfDone is the sole broadcaster.
    const { postState } = useStatusApi(practiceId, cellData, onRefresh || onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    // Legacy fallback used only when onSelectedRow not provided (TaskDashboard standalone)
    const { triggerContact, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({
        practiceId,
        // onClose uses the refresh-only callback (handleRefresh(false)) so cancel never
        // fires broadcastTaskCompleting. The genuine path fires it via postState(YES)
        // → notifyTaskCompletingIfDone. Falls back to onUpdate in the table context
        // (refreshTaskChecklist) which also has no animation side-effects.
        onClose: () => { const cb = onRefresh || onUpdate; if (cb) cb(); },
        v2TaskId: cellData?.task_id,
    });

    // Compute signals before the early-return so hooks are always called in the same order.
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const currentStatus = leadData?.status || leadData?.originalData?.status || '';
    // Contacted-count for the current status — increments when a phone-call note is saved.
    const currentContactedCount = (leadData?.lead_contacted_counts || [])
        .find((e) => e.status === currentStatus)?.count ?? 0;
    useEffect(() => {
        const watch = completionWatchRef.current;
        if (!watch) return;
        // Guard: cell may be recycled for a different lead by DataGrid Pro.
        if (watch.leadId !== leadId) { completionWatchRef.current = null; return; }
        // Genuine completion: a phone-call note was saved → contacted count increased.
        if (currentContactedCount > watch.beforeContactedCount) {
            completionWatchRef.current = null;
            postState(YES, { source: 'legacy_followup', action: 'log_phone_call' })
                .then(() => notifyTaskCompletingIfDone(practiceId, cellData?.task_id, cellData?.checklist_item_id))
                .catch(() => {});
        }
    }, [currentContactedCount, leadId]);

    const phone = leadData?.phone || leadData?.lead_contact?.phone || leadData?.originalData?.lead_contact?.phone;
    if (!phone) return <DisabledCell tooltip="Lead has no phone number" />;

    const handleTrigger = (event) => {
        // Preferred path inside NewLead context: mirror the V1 follow-up button.
        // It dispatches followUpSlice (which fetches the full lead + populates
        // Redux), then calls onSelectedRow(row, 'contactLead', true, ...) which
        // hits NewLead.jsx:1972 case → onOpenContactLead(row) → popup mounts
        // with full lead data, treatment context, sidebar history.
        if (onSelectedRow && leadId) {
            // Arm the watch BEFORE opening the popup so the useEffect can detect
            // a genuine completion (contacted count increases) when the parent
            // table re-renders with updated lead data after the popup closes.
            completionWatchRef.current = { beforeContactedCount: currentContactedCount, leadId };
            dispatch(followUpSlice(leadId, practiceId, undefined, () => {
                // Pass v2TaskId in the event object so NewLead can complete it on genuine save.
                onSelectedRow(leadData, 'contactLead', true, { ...(event || {}), v2TaskId: cellData?.task_id });
            }));
            return;
        }
        // Fallback for callers without NewLead context (TaskDashboard standalone).
        // Arm the watch before opening the popup so cancel does NOT complete.
        completionWatchRef.current = { beforeContactedCount: currentContactedCount, leadId };
        const reminder = buildReminderFromLeadData(leadData);
        triggerContact(leadId, reminder);
    };

    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:phone-call-outline" onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {!onSelectedRow && dialogs}
        </>
    );
}
LogPhoneCallCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, onSelectedRow: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// ADD_NOTE — bespoke SimpleTextDialog (keeps note save local; no Redux-lead risk)
// -----------------------------------------------
function AddNoteCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));

    const handleSave = async (text) => {
        if (!text || !leadId) return;
        setSaving(true);
        try {
            await assertApiOk(callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'POST', {
                lead: leadId, note: 'OTHER', additional_notes: text,
            }), 'Note save failed');
            await postState(YES, { note: text });
            setOpen(false);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to save note — please try again.' }));
        } finally { setSaving(false); }
    };

    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:file-text-outline" onActionClick={() => setOpen(true)} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            <SimpleTextDialog open={open} onClose={() => setOpen(false)} title="Add note" placeholder="Enter note…" onSave={handleSave} loading={saving} />
        </>
    );
}
AddNoteCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// SEND_CONSENT_FORM — V2: canonical SendFormsDialog; V1: bespoke picker
// -----------------------------------------------
function SendConsentFormCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const [forms, setForms] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [sending, setSending] = useState(false);
    // Consent status chip — fetched once when the cell is in YES state and a
    // consent_request_id is stored in response_payload. null = not yet fetched
    // or no ID available (renders generic green tick as fallback).
    const [consentStatusCfg, setConsentStatusCfg] = useState(null);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const isMobile = useResponsive('down', 'sm');
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    // Hook must be called unconditionally — early return for no-email is below.
    const { v2Enabled } = useFormsFlags();

    // Derive the stored consent request ID and completion state (both needed
    // unconditionally so the effect below can be called before any early return).
    const consentRequestId = cellData.response_payload?.consent_request_id ?? null;
    const consentCellState = getChecklistItemCompletionState(cellData);

    // Fetch consent request status once when the cell lands in YES state.
    // Phase 1: one-shot on mount / dep change — no polling.
    // Clears stale cfg first so a re-send never briefly shows the previous
    // request's status. isCancelled ref guards against setState after unmount.
    useEffect(() => {
        setConsentStatusCfg(null);
        if (consentCellState !== YES) return undefined;
        if (!consentRequestId || !practiceId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/consent-requests/${consentRequestId}/`
                );
                if (!cancelled && res.ok) {
                    const cfg = CONSENT_STATUS_CONFIG[res.data?.status];
                    if (cfg) setConsentStatusCfg(cfg);
                }
            } catch {
                // silent — generic YES icon remains
            }
        })();
        return () => { cancelled = true; };
    }, [practiceId, consentRequestId, consentCellState]);

    const loadForms = useCallback(async () => {
        if (forms.length > 0) return;
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/consent-forms/?limit=50`);
            if (res.ok) setForms(res.data?.results || res.data || []);
        } catch { /* silent */ }
    }, [practiceId, forms.length]);

    const handleOpen = () => { setOpen(true); if (!v2Enabled) loadForms(); };

    // V1-only: bespoke send handler (retained for non-V2 practices)
    const handleSendV1 = async () => {
        if (!selectedFormId || !leadId) return;
        setSending(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/consent-requests/`,
                'POST',
                {
                    form: Number(selectedFormId),
                    lead: leadId,
                    channel: 'EMAIL',
                }
            ), 'Send failed');
            await postState(YES, { form_id: selectedFormId, channel: 'EMAIL' });
            setOpen(false);
            setSelectedFormId('');
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to send consent form — please try again.' }));
        } finally {
            setSending(false);
        }
    };

    if (v2Enabled) {
        // V2: delegate to the canonical SendFormsDialog. initialLead pre-populates
        // the patient selector; onSuccess marks the checklist item YES and stores
        // the consent_request_id so the status chip can be shown on next render.
        // No email pre-check here — SendFormsDialog performs its own patient/email
        // validation. The pre-check would also falsely disable the cell in slideout
        // and detail-page contexts where task.lead is a minimal object without
        // lead_contact (even for leads that have a valid email address).
        return (
            <>
                <ChecklistCell
                    cellData={cellData}
                    practiceId={practiceId}
                    leadData={leadData}
                    onUpdate={onUpdate}
                    actionIcon="eva:file-text-outline"
                    onActionClick={() => setOpen(true)}
                    consentStatusCfg={consentStatusCfg}
                    onChecklistComplete={onChecklistComplete}
                    onChecklistReopen={onChecklistReopen}
                />
                <SendFormsDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    practiceId={practiceId}
                    initialLead={leadData}
                    onSuccess={async (payload) => {
                        // Match the sent form to this lead via lead_id; fall back to
                        // first item if lead_id matching fails (e.g. ANONYMOUS mode).
                        const requests = payload?.consentRequests || [];
                        const matched = requests.find(
                            (cr) => cr.lead_id != null && Number(cr.lead_id) === Number(leadId)
                        );
                        // Only fall back to requests[0] for single-recipient sends
                        // (anonymous / single-patient). Multi-recipient sends must
                        // match explicitly — wrong-lead attribution is worse than null.
                        const myRequest = matched ?? (requests.length === 1 ? requests[0] : null);
                        const consentRequestId = myRequest?.id ?? null;
                        try {
                            await postState(YES, { channel: 'consent_form_sent', consent_request_id: consentRequestId });
                        } catch {
                            dispatch(updateError({ success: false, message: 'Form sent but checklist mark failed — please refresh.' }));
                        }
                    }}
                />
            </>
        );
    }

    // V1 fallback: bespoke simple picker posts directly to /consent-requests/ via
    // EMAIL channel — gate on email here since the V1 flow requires it upfront.
    const emailForConsent = leadData?.email || leadData?.lead_contact?.email || leadData?.originalData?.lead_contact?.email;
    if (!emailForConsent) return <DisabledCell tooltip="Lead has no email — consent form is sent via email" />;

    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:file-text-outline" onActionClick={handleOpen} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Send consent form</DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.85rem' }}>Select form</InputLabel>
                        <Select label="Select form" value={selectedFormId} onChange={(e) => setSelectedFormId(e.target.value)} sx={{ fontSize: '0.85rem', borderRadius: 1.5 }}>
                            {forms.map((f) => (
                                <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.85rem' }}>{f.name || f.title}</MenuItem>
                            ))}
                            {forms.length === 0 && <MenuItem disabled sx={{ fontSize: '0.85rem' }}>No consent forms configured</MenuItem>}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>Cancel</Button>
                    <Button onClick={handleSendV1} variant="contained" disabled={!selectedFormId || sending} sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                        {sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
SendConsentFormCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// SEND_REVIEW_REQUEST — opens GoogleReviewSendDialog; marks complete only on successful send
// -----------------------------------------------
function SendReviewRequestCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const {
        templateData: googleReviewTemplateData,
        isSending: googleReviewSending,
        sendRequestBulk: sendGoogleReviewBulk,
    } = useGoogleReviewSend();

    // FIX #1: canEditTemplate — same derivation as ManageBusiness.jsx:432
    const { user } = useAuth();
    const { results: practiceUserResults } = useSelector((s) => s.practiceUser.practiceUser);
    const practiceUserEntry = practiceUserResults?.find((el) => el.user && el.user.id === user?.pk);
    const isPractitioner = !practiceUserEntry || practiceUserEntry.role === 'Practitioner';
    const canEditTemplate = !isPractitioner
        && (practiceUserEntry?.role === 'ADMIN'
            || practiceUserEntry?.role === 'TCO'
            || Boolean(user?.is_superuser));

    const hasEmail = Boolean(leadData?.email || leadData?.lead_contact?.email || leadData?.originalData?.lead_contact?.email);
    const hasPhone = Boolean(leadData?.phone || leadData?.lead_contact?.phone || leadData?.originalData?.lead_contact?.phone);
    if (!hasEmail && !hasPhone) {
        return <DisabledCell tooltip="Lead has no email or phone — cannot send review request" />;
    }

    const ourLeadId = (leadData?.lead_id || String(leadData?.id ?? '').split('-')[0]);

    const handleSend = (entries, opts) => sendGoogleReviewBulk(entries, {
        ...opts,
        onSuccess: async (outcomes) => {
            if (opts?.onSuccess) opts.onSuccess(outcomes);
            // FIX #4 (critical): only mark complete if THIS lead was among the successful sends.
            // Prevents bulk-send to other leads from falsely completing this cell.
            const ourLeadSucceeded = (outcomes?.sent || []).some(
                (l) => l?.id === ourLeadId || l?.lead_id === ourLeadId
            );
            if (ourLeadSucceeded) {
                // FIX #2: use actual template channel instead of hardcoded 'email'
                const channel = googleReviewTemplateData?.channel || 'email';
                await postState(YES, { channel, sent_at: new Date().toISOString() });
                // FIX #3: do NOT auto-close — let STEP_RESULTS render so staff sees outcomes
            }
        },
    });


    return (
        <>
            <ChecklistCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon="eva:star-outline"
                onActionClick={() => setDialogOpen(true)}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
            <GoogleReviewSendDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                practiceId={practiceId}
                isSending={googleReviewSending}
                onSend={handleSend}
                templateData={googleReviewTemplateData}
                canEditTemplate={canEditTemplate}
                prefilledLead={leadData}
            />
        </>
    );
}
SendReviewRequestCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// SEND_PAYMENT_LINK — Stripe-aware payment cell (#191)
//
// Two render branches gated on practice.is_stripe_enabled (read-only field on
// PracticeSerializer). One component, two flows; never two cell components.
//
//   STRIPE CONNECTED (default for live practices)
//     Main button  → "Send Payment Link" — opens amount dialog, POSTs Stripe
//                    checkout session, then stamps the cell as YES.
//     Arrow menu   → "Mark as paid (no link)" — opens the manual-acknowledge
//                    dialog and stamps method='manual' without touching Stripe.
//
//   STRIPE NOT CONNECTED
//     Main button  → "Mark as paid" — opens the manual-acknowledge dialog
//                    directly. No Stripe call possible.
//     No arrow menu — there are no Stripe options to offer.
//
// All money values flow through the wire as integer pence. The dialog accepts
// pounds.pence in the input but multiplies x100 → integer before POST so the
// BE only ever sees `amount_pence: <int>`. (#191 brief constraint.)
// -----------------------------------------------
function SendPaymentLinkCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [stripeOpen, setStripeOpen] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [stripeAmount, setStripeAmount] = useState('');
    const [sending, setSending] = useState(false);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const isMobile = useResponsive('down', 'sm');

    // Source of truth: PracticeSerializer.is_stripe_enabled (read-only field
    // computed from StripeAccount.is_enabled && account_id != ''). Default to
    // true if the field is absent so legacy clients don't lose the Stripe path.
    const isStripeEnabled = useSelector((state) => {
        const v = state?.practice?.practice?.is_stripe_enabled;
        return v === undefined || v === null ? true : Boolean(v);
    });

    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const treatmentId = leadData?.treatment_id || leadData?.lead_treatment?.id || leadData?.originalData?.lead_treatment?.id;
    const treatmentName = leadData?.treatment || leadData?.lead_treatment?.name || 'this treatment';

    if (!treatmentId) {
        return <DisabledCell tooltip="Lead has no treatment to bill for" />;
    }

    // Stripe path — preserves the existing endpoint exactly. We still POST the
    // checkout session, then stamp the cell YES with mode='stripe'. No payload
    // change here; this is the same wire the cell shipped with on staging today.
    const handleStripeSend = async () => {
        const parsedAmount = parseFloat(stripeAmount);
        if (!parsedAmount || parsedAmount <= 0 || !leadId || !treatmentId) return;
        setSending(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/lead-treatments/${treatmentId}/stripe-checkout-sessions/`,
                'POST',
                { amount: parsedAmount },
            ), 'Stripe checkout failed');
            await postState(YES, { amount: parsedAmount, mode: 'stripe' });
            setStripeOpen(false);
            setStripeAmount('');
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to send payment link — please try again.' }));
        } finally {
            setSending(false);
        }
    };

    // Manual-acknowledge path — never calls Stripe. Sends integer pence on the
    // wire so we never round-trip through floats. BE validates `amount_pence`
    // is a positive integer and stamps method='manual' + acknowledged_at +
    // acknowledged_by_practice_user before recording the lead-activity row.
    const handleManualConfirm = async ({ amountPence, note }) => {
        setSending(true);
        try {
            const payload = {
                method: 'manual',
                amount_pence: amountPence,
                acknowledged_at: new Date().toISOString(),
            };
            if (note) payload.note = note;
            await postState(YES, payload);
            setManualOpen(false);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to record payment — please try again.' }));
        } finally {
            setSending(false);
        }
    };

    // ── Stripe-disabled branch — single button, single dialog ──
    if (!isStripeEnabled) {
        return (
            <>
                <ChecklistCell
                    cellData={cellData}
                    practiceId={practiceId}
                    leadData={leadData}
                    onUpdate={onUpdate}
                    actionIcon="eva:checkmark-circle-2-outline"
                    onActionClick={() => setManualOpen(true)}
                    onChecklistComplete={onChecklistComplete}
                    onChecklistReopen={onChecklistReopen}
                />
                <PaymentManualAcknowledgeDialog
                    open={manualOpen}
                    onClose={() => setManualOpen(false)}
                    treatmentName={treatmentName}
                    onConfirm={handleManualConfirm}
                    sending={sending}
                />
            </>
        );
    }

    // ── Stripe-enabled branch — main "Send link" + secondary "Mark as paid" ──
    // The dropdown shows: [Send payment link] / [Mark as paid (no link)] / state options.
    // Saved templates can be appended to secondaryActions in a follow-up.
    return (
        <>
            <ChecklistCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon="eva:credit-card-outline"
                onActionClick={() => setStripeOpen(true)}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />

            {/* Stripe send-link dialog — unchanged behaviour from prior cell. */}
            <Dialog open={stripeOpen} onClose={() => setStripeOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Send payment link</DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Stack spacing={1.5}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                            Treatment: <strong>{treatmentName}</strong>
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label="Amount (£)"
                            type="number"
                            inputProps={{ min: 0.01, step: 0.01 }}
                            value={stripeAmount}
                            onChange={(e) => setStripeAmount(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setStripeOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>Cancel</Button>
                    <Button onClick={handleStripeSend} variant="contained" disabled={!stripeAmount || parseFloat(stripeAmount) <= 0 || sending} sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                        {sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Send link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manual-acknowledge sub-dialog — opened from the arrow menu. */}
            <PaymentManualAcknowledgeDialog
                open={manualOpen}
                onClose={() => setManualOpen(false)}
                treatmentName={treatmentName}
                onConfirm={handleManualConfirm}
                sending={sending}
            />
        </>
    );
}
SendPaymentLinkCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// SCHEDULE_REMINDER — canonical AddReminderPopup
// (routes to V2 CreateTaskDialog or V1 AddReminder based on tasks-v2 flag)
// -----------------------------------------------
function ScheduleReminderCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);

    const handleSuccess = async () => {
        try {
            await postState(YES);
        } catch {
            dispatch(updateError({ success: false, message: 'Reminder created but checklist mark failed — please refresh.' }));
        }
    };

    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:bell-outline" onActionClick={() => setOpen(true)} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {/* AddReminderPopup already gates on tasks-v2: V2 → CreateTaskDialog,
                V1 → legacy AddReminder. onSuccess marks the checklist item YES
                in both paths. */}
            <AddReminderPopup
                open={open}
                onClose={() => setOpen(false)}
                leadDetail={leadData}
                onSuccess={handleSuccess}
            />
        </>
    );
}
ScheduleReminderCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// OPEN_FOLLOW_UP — log a follow-up note with channel choice
// -----------------------------------------------
function OpenFollowUpCell({ cellData, practiceId, leadData, onUpdate, onRefresh, actionIcon: actionIconProp, onChecklistComplete, onChecklistReopen }) {
    const completionWatchRef = useRef(null);
    const { postState } = useStatusApi(practiceId, cellData, onRefresh || onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const { triggerFollowUp, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({
        practiceId,
        // onClose uses refresh-only callback — no broadcast on cancel.
        onClose: () => { const cb = onRefresh || onUpdate; if (cb) cb(); },
        v2TaskId: cellData?.task_id,
    });
    const cellIcon = actionIconProp || 'eva:person-done-outline';
    const leadStatus = leadData?.status || leadData?.originalData?.lead_treatment?.status || '';
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const leadTreatment = leadData?.originalData?.lead_treatment || leadData?.lead_treatment || { id: leadData?.treatment_id };
    // Contacted-count for the current status — increments when a follow-up note is logged.
    const currentContactedCount = (leadData?.lead_contacted_counts || [])
        .find((e) => e.status === leadStatus)?.count ?? 0;

    // Genuine completion: lead status changed (e.g. booked during follow-up) OR
    // the contacted count for this status increased (follow-up note logged, no status change).
    useEffect(() => {
        const watch = completionWatchRef.current;
        if (!watch) return;
        if (watch.leadId !== leadId) { completionWatchRef.current = null; return; }
        const statusChanged = leadStatus && leadStatus !== watch.beforeStatus;
        const countIncreased = currentContactedCount > watch.beforeContactedCount;
        if (statusChanged || countIncreased) {
            completionWatchRef.current = null;
            postState(YES, { source: 'legacy_followup', action: 'open_follow_up' })
                .then(() => notifyTaskCompletingIfDone(practiceId, cellData?.task_id, cellData?.checklist_item_id))
                .catch(() => {});
        }
    }, [leadStatus, currentContactedCount, leadId]);

    const handleTrigger = () => {
        completionWatchRef.current = { beforeStatus: leadStatus, beforeContactedCount: currentContactedCount, leadId };
        const reminder = buildReminderFromLeadData(leadData);
        triggerFollowUp(leadStatus, leadId, reminder, leadTreatment, false);
    };


    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon={cellIcon} onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {dialogs}
        </>
    );
}
OpenFollowUpCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, actionIcon: PropTypes.string, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// BOOK_APPOINTMENT — legacy addAppointment thunk + booked-with / assigned-to selects
// -----------------------------------------------
function BookAppointmentCell({ cellData, practiceId, leadData, onUpdate, onRefresh, onChecklistComplete, onChecklistReopen }) {
    const completionWatchRef = useRef(null);
    const { postState } = useStatusApi(practiceId, cellData, onRefresh || onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const { triggerContact, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({
        practiceId,
        // onClose uses refresh-only callback — no broadcast on cancel.
        onClose: (outcome) => {
            const shouldComplete = settleBookAppointmentDialog(completionWatchRef, outcome);
            if (shouldComplete) {
                postState(YES, { source: 'legacy_followup', action: 'book_appointment' })
                    .then(() => notifyTaskCompletingIfDone(practiceId, cellData?.task_id, cellData?.checklist_item_id))
                    .catch(() => {});
            }
            const cb = onRefresh || onUpdate;
            if (cb) cb();
        },
        v2TaskId: cellData?.task_id,
        completionOwnedExternally: true,
    });
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const currentStatus = leadData?.status || leadData?.originalData?.status;

    const handleTrigger = () => {
        armBookAppointmentCompletionWatch(completionWatchRef, { beforeStatus: currentStatus, leadId });
        const reminder = buildReminderFromLeadData(leadData);
        // Always opens OpenContactLeadPopup (V1 follow-up → appointment booking)
        triggerContact(leadId, reminder);
    };


    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:calendar-outline" onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {dialogs}
        </>
    );
}
BookAppointmentCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// RECORD_ATTENDANCE — confirm attendance on active booked appointment
// -----------------------------------------------
function RecordAttendanceCell({ cellData, practiceId, leadData, onUpdate, onRefresh, onChecklistComplete, onChecklistReopen }) {
    // Hooks must precede early returns (rules-of-hooks).
    const completionWatchRef = useRef(null);
    const { postState } = useStatusApi(practiceId, cellData, onRefresh || onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const { triggerAttendClick, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({
        practiceId,
        // onClose uses refresh-only callback — no broadcast on cancel.
        onClose: () => { const cb = onRefresh || onUpdate; if (cb) cb(); },
        v2TaskId: cellData?.task_id,
    });
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const leadStatus = leadData?.status || leadData?.originalData?.status;

    // Genuine completion: attendance recorded → status transitions to CONSULTATION_ATTENDED.
    useEffect(() => {
        const watch = completionWatchRef.current;
        if (!watch) return;
        if (watch.leadId !== leadId) { completionWatchRef.current = null; return; }
        if (leadStatus && leadStatus !== watch.beforeStatus) {
            completionWatchRef.current = null;
            postState(YES, { source: 'legacy_followup', action: 'record_attendance' })
                .then(() => notifyTaskCompletingIfDone(practiceId, cellData?.task_id, cellData?.checklist_item_id))
                .catch(() => {});
        }
    }, [leadStatus, leadId]);

    const appts = leadData?.appointments?.length
        ? leadData.appointments
        : leadData?.originalData?.lead_appointment || [];
    const bookedAppt = appts.find((a) => a?.status === 'BOOKED' || a?.status === 'CONSULTATION_BOOKED') || appts[0];

    if (cellData?.appointment_already_attended) {
        return <DisabledCell tooltip="Attendance has already been recorded for this appointment." />;
    }

    if (leadStatus !== 'CONSULTATION_BOOKED' || !bookedAppt?.id) {
        const currentLabel = leadStatus ? leadStatus.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';
        return <DisabledCell tooltip={`Lead must be in Consultation Booked status to record attendance. Currently: ${currentLabel}.`} />;
    }

    const handleTrigger = () => {
        completionWatchRef.current = { beforeStatus: leadStatus, leadId };
        const reminder = buildReminderFromLeadData(leadData);
        triggerAttendClick(leadId, reminder);
    };


    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="akar-icons:check" onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {dialogs}
        </>
    );
}
RecordAttendanceCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// RECORD_FAILED_TO_ATTEND — mark active booked appointment as FTA
// -----------------------------------------------
function RecordFailedToAttendCell({ cellData, practiceId, leadData, onUpdate, onRefresh, onChecklistComplete, onChecklistReopen }) {
    // Hooks must precede early returns (rules-of-hooks).
    const completionWatchRef = useRef(null);
    const { postState } = useStatusApi(practiceId, cellData, onRefresh || onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const { triggerAttendFailedClick, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({
        v2TaskId: cellData?.task_id,
        practiceId,
        // onClose uses refresh-only callback — no broadcast on cancel.
        onClose: () => { const cb = onRefresh || onUpdate; if (cb) cb(); },
    });
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const leadStatus = leadData?.status || leadData?.originalData?.status;

    // Genuine completion: FTA recorded → status transitions away from CONSULTATION_BOOKED.
    useEffect(() => {
        const watch = completionWatchRef.current;
        if (!watch) return;
        if (watch.leadId !== leadId) { completionWatchRef.current = null; return; }
        if (leadStatus && leadStatus !== watch.beforeStatus) {
            completionWatchRef.current = null;
            postState(YES, { source: 'legacy_followup', action: 'record_fta' })
                .then(() => notifyTaskCompletingIfDone(practiceId, cellData?.task_id, cellData?.checklist_item_id))
                .catch(() => {});
        }
    }, [leadStatus, leadId]);

    const appts = leadData?.appointments?.length
        ? leadData.appointments
        : leadData?.originalData?.lead_appointment || [];
    const bookedAppt = appts.find((a) => a?.status === 'BOOKED' || a?.status === 'CONSULTATION_BOOKED') || appts[0];

    if (leadStatus !== 'CONSULTATION_BOOKED' || !bookedAppt?.id) {
        const currentLabel = leadStatus ? leadStatus.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';
        return <DisabledCell tooltip={`Lead must be in Consultation Booked status to record failed to attend. Currently: ${currentLabel}.`} />;
    }

    const handleTrigger = () => {
        completionWatchRef.current = { beforeStatus: leadStatus, leadId };
        const reminder = buildReminderFromLeadData(leadData);
        triggerAttendFailedClick(leadId, reminder);
    };


    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="ci:close-big" onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {dialogs}
        </>
    );
}
RecordFailedToAttendCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// START_TREATMENT — StartTreatmentPopup (legacy) + StartingTreatment Redux thunk
// -----------------------------------------------
function StartTreatmentCell({ cellData, practiceId, leadData, onUpdate, onRefresh, onChecklistComplete, onChecklistReopen }) {
    // Hooks must precede early returns (rules-of-hooks).
    const completionWatchRef = useRef(null);
    const { postState } = useStatusApi(practiceId, cellData, onRefresh || onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const { triggerAttendedPopup, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({
        practiceId,
        // onClose uses refresh-only callback — no broadcast on cancel.
        onClose: () => { const cb = onRefresh || onUpdate; if (cb) cb(); },
        v2TaskId: cellData?.task_id,
    });
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const treatmentId = leadData?.treatment_id || leadData?.originalData?.lead_treatment?.id;
    const leadStatus = leadData?.status || leadData?.originalData?.status;

    // Genuine completion: treatment started → status transitions to TREATMENT_STARTED.
    useEffect(() => {
        const watch = completionWatchRef.current;
        if (!watch) return;
        if (watch.leadId !== leadId) { completionWatchRef.current = null; return; }
        if (leadStatus && leadStatus !== watch.beforeStatus) {
            completionWatchRef.current = null;
            postState(YES, { source: 'legacy_followup', action: 'start_treatment' })
                .then(() => notifyTaskCompletingIfDone(practiceId, cellData?.task_id, cellData?.checklist_item_id))
                .catch(() => {});
        }
    }, [leadStatus, leadId]);

    if (leadStatus !== 'CONSULTATION_ATTENDED' || !treatmentId) {
        return <DisabledCell tooltip="Only available for Consultation Attended leads with a treatment" />;
    }

    const handleTrigger = () => {
        completionWatchRef.current = { beforeStatus: leadStatus, leadId };
        const reminder = buildReminderFromLeadData(leadData);
        triggerAttendedPopup(leadId, reminder);
    };


    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:play-circle-outline" onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {dialogs}
        </>
    );
}
StartTreatmentCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// COMPLETE_TREATMENT — CustomCompleteTreatmentDialog (legacy) + CompleteTreatment Redux thunk
// -----------------------------------------------
function CompleteTreatmentCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const { triggerFollowUp, buildReminderFromLeadData, dialogs } = useLegacyFollowUpFlow({ practiceId });
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const treatmentId = leadData?.treatment_id || leadData?.originalData?.lead_treatment?.id;
    const leadStatus = leadData?.status || leadData?.originalData?.status;
    const leadTreatment = leadData?.originalData?.lead_treatment || { id: treatmentId };

    if (leadStatus !== 'TREATMENT_STARTED' || !treatmentId) {
        return <DisabledCell tooltip="Only available for Treatment Started leads with a treatment" />;
    }

    const handleTrigger = () => {
        const reminder = buildReminderFromLeadData(leadData);
        // V1 navigates to lead detail for TREATMENT_STARTED status
        triggerFollowUp('TREATMENT_STARTED', leadId, reminder, leadTreatment, false);
    };


    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:checkmark-circle-outline" onActionClick={handleTrigger} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            {dialogs}
        </>
    );
}
CompleteTreatmentCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// SEND_TREATMENT_PLAN — confirm dialog → POST share-plan endpoint
// -----------------------------------------------
function SendTreatmentPlanCell({ cellData, colMeta, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    // null = loading, [] = no plans, [...] = plans exist
    const [treatmentPlans, setTreatmentPlans] = useState(null);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const isMobile = useResponsive('down', 'sm');
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const treatmentId = leadData?.treatment_id || leadData?.lead_treatment?.id || leadData?.originalData?.lead_treatment?.id;
    const email = leadData?.email || leadData?.lead_contact?.email || leadData?.originalData?.lead_contact?.email;

    // Fetch treatment plans on mount so we can gate the share action on their existence.
    // We do this locally (not from Redux formDeliveries) because that slice is a shared
    // singleton that may hold plans from a different lead currently open in the slideout.
    useEffect(() => {
        if (!practiceId || !leadId) return undefined;
        let cancelled = false;
        callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/?ordering=-created_at&limit=1`)
            .then((res) => {
                if (cancelled) return;
                setTreatmentPlans(res?.data?.results || []);
            })
            .catch(() => {
                if (!cancelled) setTreatmentPlans([]);
            });
        return () => { cancelled = true; };
    }, [practiceId, leadId]);

    if (!email || !treatmentId) {
        return <DisabledCell tooltip={!email ? 'Lead has no email address' : 'Lead has no active treatment'} />;
    }

    // While plans are loading (null), render the cell normally — avoids a flash of disabled state.
    // Once loaded: if the lead has no plans, disable the cell with a directed tooltip.
    if (treatmentPlans !== null && treatmentPlans.length === 0) {
        return <DisabledCell tooltip="No treatment plan to share — create one on the lead's Treatments tab first" />;
    }

    const handleSend = async () => {
        setSending(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/lead-treatments/${treatmentId}/share-plan/`,
                'POST',
                {}
            ), 'Share failed');
            await postState(YES);
            setOpen(false);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to send treatment plan — please try again.' }));
        } finally {
            setSending(false);
        }
    };

    const title = colMeta?.column_label || cellData?.column_label || 'Send treatment plan';

    return (
        <>
            <ChecklistCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} actionIcon="eva:file-text-outline" onActionClick={() => setOpen(true)} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>{title}</DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        Email treatment plan to <strong>{email}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>Cancel</Button>
                    <Button onClick={handleSend} variant="contained" disabled={sending} sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                        {sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
SendTreatmentPlanCell.propTypes = { cellData: PropTypes.object.isRequired, colMeta: PropTypes.object, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// UPLOAD_DOCUMENT — file picker → multipart POST → mark complete
// -----------------------------------------------
const UPLOAD_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const UPLOAD_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.heic,.txt,.csv,.xls,.xlsx';

function UploadDocumentCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const state = getChecklistItemCompletionState(cellData);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > UPLOAD_MAX_BYTES) {
            dispatch(updateError({ success: false, message: `File too large — maximum 10 MB (selected: ${(file.size / 1024 / 1024).toFixed(1)} MB).` }));
            return;
        }
        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const uploadRes = await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/leads-media/`,
                'POST',
                { lead: leadId, media: base64, channel: 'TASK_CHECKLIST', description: file.name }
            ), 'Upload failed');
            const leadMediaId = uploadRes?.data?.id;
            await postState(YES, { lead_media_id: leadMediaId, filename: file.name });
        } catch {
            dispatch(updateError({ success: false, message: 'Upload failed — please try again.' }));
        } finally {
            if (e.target) e.target.value = '';
        }
    };

    const openPicker = () => {
        if (state !== PENDING) return;
        document.getElementById(`upload-doc-${cellData.id}`)?.click();
    };

    const filename = cellData?.response_payload?.filename || null;


    return (
        <>
            <input
                id={`upload-doc-${cellData.id}`}
                type="file"
                accept={UPLOAD_ACCEPT}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <ChecklistCell
                cellData={filename ? { ...cellData, _display_label: filename } : cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon="eva:upload-outline"
                onActionClick={openPicker}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
        </>
    );
}
UploadDocumentCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// LINKED_CHECKLIST — shows "X of Y complete" chip, click → open TaskSlideout
// -----------------------------------------------
function LinkedChecklistCell({ cellData, colMeta, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [taskId, setTaskId] = useState(null);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const targetTemplateId = colMeta?.action_config?.target_template_id || cellData?.action_config?.target_template_id;
    const state = getChecklistItemCompletionState(cellData);

    useEffect(() => {
        if (!leadId || !targetTemplateId || !practiceId) return;
        let cancelled = false;
        callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-template-instances/?status=active`, 'GET')
            .then((res) => {
                if (cancelled) return;
                const results = res.data?.results || res.data || [];
                const match = results.find((t) => t.template_id === targetTemplateId || t.template === targetTemplateId);
                if (match) {
                    setTaskId(match.id);
                    const done = (match.checklist_items || []).filter((it) => it.state === YES).length;
                    const total = (match.checklist_items || []).length;
                    if (done === total && total > 0 && state === PENDING) {
                        postState(YES).catch(() => {});
                    }
                }
            })
            .catch(() => {});
        return () => { cancelled = true; }; // eslint-disable-line consistent-return
    }, [leadId, targetTemplateId, practiceId]);

    if (!targetTemplateId) {
        return <DisabledCell tooltip="No target template configured for this cell" />;
    }

    return (
        <ChecklistCell
            cellData={taskId ? { ...cellData, linked_task_id: taskId } : cellData}
            practiceId={practiceId}
            leadData={leadData}
            onUpdate={onUpdate}
            actionIcon="eva:link-outline"
            onActionClick={() => {}}
            onChecklistComplete={onChecklistComplete}
            onChecklistReopen={onChecklistReopen}
        />
    );
}
LinkedChecklistCell.propTypes = { cellData: PropTypes.object.isRequired, colMeta: PropTypes.object, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// LEAD_STATUS_CHANGED — "Move to {Status}" button → POST transition
// -----------------------------------------------
const STATUS_TRANSITION_GRAPH = {
    NEW: ['CONSULTATION_BOOKED'],
    CONSULTATION_BOOKED: ['CONSULTATION_ATTENDED', 'FAILED_TO_ATTEND_CONSULTATION'],
    CONSULTATION_ATTENDED: ['TREATMENT_STARTED'],
    FAILED_TO_ATTEND_CONSULTATION: ['NEW', 'CONSULTATION_BOOKED'],
    TREATMENT_STARTED: ['CLOSED'],
    CLOSED: [],
};

const STATUS_DISPLAY_LABELS = {
    NEW: 'New',
    CONSULTATION_BOOKED: 'Consultation Booked',
    CONSULTATION_ATTENDED: 'Consultation Attended',
    FAILED_TO_ATTEND_CONSULTATION: 'Failed to Attend',
    TREATMENT_STARTED: 'Treatment Started',
    CLOSED: 'Completed',
};

function LeadStatusChangedCell({ cellData, colMeta, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [saving, setSaving] = useState(false);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const treatmentId = checklistTreatmentId({ item: cellData, leadData });
    const currentStatus = leadData?.status || leadData?.originalData?.status;
    const targetStatus = colMeta?.action_config?.target_status || cellData?.action_config?.target_status;

    const validTransitions = STATUS_TRANSITION_GRAPH[currentStatus] || [];
    const isValidTransition = targetStatus && validTransitions.includes(targetStatus);

    if (!treatmentId) return <DisabledCell tooltip="Treatment context missing — please reload" />;
    if (!targetStatus) {
        return <DisabledCell tooltip="No target status configured for this cell" />;
    }
    if (!isValidTransition) {
        return <DisabledCell tooltip={`Cannot move from ${STATUS_DISPLAY_LABELS[currentStatus] || currentStatus} to ${STATUS_DISPLAY_LABELS[targetStatus] || targetStatus}`} />;
    }

    const handleMove = async () => {
        if (!leadId || !treatmentId || saving) return;
        setSaving(true);
        try {
            const outcome = await captureChecklistTransition({ practiceId, leadId, treatmentId, targetStatus });
            if (outcome.status !== 'success') return;
            dispatch(invalidateAttendance({ practiceId, leadId, treatmentId }));
            await postState(YES, { target_status: targetStatus });
            if (onUpdate) onUpdate();
        } catch {
            dispatch(updateError({ success: false, message: `Failed to move lead to ${STATUS_DISPLAY_LABELS[targetStatus] || targetStatus} — please try again.` }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ChecklistCell
            cellData={cellData}
            practiceId={practiceId}
            leadData={leadData}
            onUpdate={onUpdate}
            actionIcon="eva:swap-outline"
            onActionClick={handleMove}
            onChecklistComplete={onChecklistComplete}
            onChecklistReopen={onChecklistReopen}
        />
    );
}
LeadStatusChangedCell.propTypes = { cellData: PropTypes.object.isRequired, colMeta: PropTypes.object, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// LEAD_NEXT_ACTION — dynamic "next step" button derived from lead status
// Mirrors getForwardActions() in LeadActionZone without importing the full component.
// -----------------------------------------------

const JOURNEY_ORDER = ['NEW', 'CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'];
const getJourneyIdx = (s) => JOURNEY_ORDER.indexOf(s);

// FTA can arrive as either form depending on the source field (Lead FSM vs Template).
const FTA_STATUSES = new Set(['FAILED_TO_ATTEND', 'FAILED_TO_ATTEND_CONSULTATION']);
const isBookedOrFTA = (s) => s === 'CONSULTATION_BOOKED' || FTA_STATUSES.has(s);

// Single point of truth for the Follow Up entry. Sits at the TOP of every
// non-CLOSED status menu because "call/contact the lead" is the most common
// day-to-day action regardless of FSM stage. Routes to the status-appropriate
// popup (OpenContactLeadPopup for NEW, OpenAttendedPopup for
// CONSULTATION_ATTENDED, etc.) via the V1 selectedRow flow.
const pushFollowUp = (actions) => {
    actions.push({ key: 'FOLLOW_UP', label: 'Follow Up', icon: 'solar:phone-calling-bold-duotone', color: '#0078FF' });
};

// Status-aware FOLLOW_UP → V1 popup-router key. Single source of truth for the
// unified "Follow Up" primary (used by the email/WhatsApp ComposerCell primary
// and mirrored by LeadNextActionCell.resolveSelectedRowKey's FOLLOW_UP branch).
//   contactLead          — NEW / CONSULTATION_BOOKED (new-lead contact wizard)
//   consultationAttended — CONSULTATION_ATTENDED / TREATMENT_STARTED (attended popup)
//   recordUnAttended     — FTA (either form)
function resolveFollowUpRowKey(status) {
    if (FTA_STATUSES.has(status)) return 'recordUnAttended';
    if (status === 'CONSULTATION_ATTENDED' || status === 'TREATMENT_STARTED') return 'consultationAttended';
    return 'contactLead';
}

function getNextActions(currentStatus) {
    const actions = [];
    // Booked or FTA: Follow Up + Record Attended + Record FTA.
    // Handled BEFORE JOURNEY lookup because FTA forms aren't in the linear JOURNEY_ORDER.
    if (isBookedOrFTA(currentStatus)) {
        pushFollowUp(actions);
        actions.push({ key: 'ATTENDED', label: 'Record Attended', icon: 'solar:check-circle-bold-duotone', color: '#22C55E' });
        actions.push({ key: 'FAILED_TO_ATTEND', label: 'Record FTA', icon: 'solar:close-circle-bold-duotone', color: '#FF5630' });
        return actions;
    }
    const idx = getJourneyIdx(currentStatus);
    if (idx === -1) return [];
    if (idx < 4) pushFollowUp(actions);
    if (idx < 1) actions.push({ key: 'BOOK', label: 'Book Consultation', icon: 'solar:calendar-bold-duotone', color: '#0078FF' });
    if (idx < 2) actions.push({ key: 'ATTENDED', label: 'Record Attended', icon: 'solar:check-circle-bold-duotone', color: '#22C55E' });
    if (idx < 3) actions.push({ key: 'START_TREATMENT', label: 'Start Treatment', icon: 'solar:heart-pulse-bold-duotone', color: '#FFAB00' });
    if (idx < 4) actions.push({ key: 'COMPLETE', label: 'Complete Treatment', icon: 'solar:verified-check-bold-duotone', color: '#00A76F' });
    return actions;
}

function LeadNextActionCell({ cellData, practiceId, leadData, onUpdate, onRefresh, onSelectedRow, onChecklistComplete, onChecklistReopen }) {
    const navigate = useNavigate();
    // Track which action key the user fired so we can also track its expected
    // completion signal. Only mark cell DONE when the popup closes AFTER a
    // status transition was observed (proxy: BE response reflects state change
    // via leadData refetch). If user cancels the popup, cell stays PENDING.
    const completionWatchRef = useRef(null);
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);

    // Legacy fallback used only when onSelectedRow is not provided (TaskDashboard standalone)
    const {
        triggerContact,
        triggerAttendedPopup,
        triggerAttendClick,
        triggerAttendFailedClick,
        buildReminderFromLeadData,

        dialogs,
    } = useLegacyFollowUpFlow({
        practiceId,
        // Cancel-safe: use handleRefresh(false) so closing without a genuine action
        // does NOT fire broadcastTaskCompleting. Genuine completion goes through the
        // completionWatchRef status-change watcher → postState(YES) → onUpdate()
        // = handleRefresh(true) → broadcast. onRefresh falls back to onUpdate in the
        // table context (refreshTaskChecklist) where there is no animation concern.
        onClose: () => { const cb = onRefresh || onUpdate; if (cb) cb(); },
        v2TaskId: cellData?.task_id,
    });

    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const leadStatus = leadData?.status || leadData?.originalData?.status;
    const treatmentId = leadData?.treatment_id || leadData?.originalData?.lead_treatment?.id;

    const actions = getNextActions(leadStatus);

    // Watch for status transition after a popup-driven action — mark cell complete
    // ONLY when the BE actually transitioned the lead status. If user cancels the
    // popup (no status change), the cell stays PENDING. This must be declared
    // before the early return below (rules-of-hooks).
    const currentStatus = leadData?.status || leadData?.originalData?.status;
    const currentLeadId = (leadData?.lead_id || String(leadData?.id ?? '').split('-')[0]);
    useEffect(() => {
        const watch = completionWatchRef.current;
        if (!watch) return;
        // Guard against DataGrid Pro cell recycling: if the cell is now showing a
        // different lead than the one we armed the watch for, drop the watch entirely.
        if (watch.leadId !== currentLeadId) {
            completionWatchRef.current = null;
            return;
        }
        if (currentStatus && currentStatus !== watch.beforeStatus) {
            completionWatchRef.current = null;
            postState(YES, { resolved_action: watch.actionKey, source: 'lead_status_transition' }).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStatus, currentLeadId]);

    if (actions.length === 0) {
        const supported = 'NEW, Consultation Booked, Attended, Failed to Attend, or Treatment Started';
        return <DisabledCell tooltip={`Next action available only on leads with status: ${supported}. Current: ${leadStatus || 'unknown'}`} />;
    }

    // Mirror V1 follow-up button (NewLeadDataGrid.jsx:389): dispatch followUpSlice
    // first to prime Redux, then in the callback fire onSelectedRow with the right
    // action name so NewLead's own popup mounts with full lead data.
    const routeViaOnSelectedRow = (event, action, selectedRowKey) => {
        completionWatchRef.current = { actionKey: action.key, beforeStatus: leadStatus, leadId };
        dispatch(followUpSlice(leadId, practiceId, undefined, () => {
            // Pass v2TaskId for contactLead routes so NewLead can complete the task on genuine save.
            const enrichedEvent = selectedRowKey === 'contactLead'
                ? { ...(event || {}), v2TaskId: cellData?.task_id }
                : event;
            onSelectedRow(leadData, selectedRowKey, true, enrichedEvent);
        }));
    };

    const resolveSelectedRowKey = (actionKey) => {
        if (actionKey === 'BOOK') return 'contactLead';
        if (actionKey === 'FAILED_TO_ATTEND') return 'recordUnAttended';
        if (actionKey === 'START_TREATMENT') return 'StartTreatmentConfirmation';
        if (actionKey === 'COMPLETE') return 'TreatmentConfirmation';
        if (actionKey === 'ATTENDED') {
            if (leadStatus === 'CONSULTATION_BOOKED' || FTA_STATUSES.has(leadStatus)) return 'recordAttended';
            return 'consultationAttended';
        }
        // FOLLOW_UP — status-aware: opens the V1 follow-up popup matching the
        // lead's current stage. Delegates to the shared resolveFollowUpRowKey so
        // the cell primary (ComposerCell) and this menu can't drift.
        if (actionKey === 'FOLLOW_UP') return resolveFollowUpRowKey(leadStatus);
        return null;
    };

    const fireAction = (actionKey, event) => {
        if (!leadId) return;
        const action = actions.find((a) => a.key === actionKey);
        if (!action) return;

        try {
            // Treatment-state actions need a treatment record
            if ((actionKey === 'START_TREATMENT' || actionKey === 'COMPLETE') && !treatmentId) {
                dispatch(updateError({ success: false, message: 'No treatment record found.' }));
                return;
            }

            if (onSelectedRow) {
                const selectedRowKey = resolveSelectedRowKey(actionKey);
                if (selectedRowKey) {
                    routeViaOnSelectedRow(event, action, selectedRowKey);
                    return;
                }
            }

            // Fallback (TaskDashboard standalone): no onSelectedRow. For treatment
            // actions, navigate to lead detail since StartTreatmentPopup +
            // CustomCompleteTreatmentDialog live in NewLead with payment-enforcement.
            if (actionKey === 'START_TREATMENT' || actionKey === 'COMPLETE') {
                navigate(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`);
                return;
            }

            // Fallback: TaskDashboard standalone — use legacy hook.
            // Arm completionWatchRef so the useEffect above marks this cell done
            // when the lead's status transitions after the popup action completes.
            // (routeViaOnSelectedRow arms it for the onSelectedRow path; we must
            // do it here too so the board / slideout path gets the same behaviour.)
            completionWatchRef.current = { actionKey, beforeStatus: leadStatus, leadId };
            const reminder = buildReminderFromLeadData(leadData);
            if (actionKey === 'BOOK') {
                triggerContact(leadId, reminder);
            } else if (actionKey === 'ATTENDED') {
                if (leadStatus === 'CONSULTATION_BOOKED' || FTA_STATUSES.has(leadStatus)) {
                    triggerAttendClick(leadId, reminder);
                } else {
                    triggerAttendedPopup(leadId, reminder);
                }
            } else if (actionKey === 'FAILED_TO_ATTEND') {
                triggerAttendFailedClick(leadId, reminder);
            } else if (actionKey === 'FOLLOW_UP') {
                // Status-aware popup: mirrors resolveSelectedRowKey() but uses the
                // legacy-hook triggers directly when no onSelectedRow is provided.
                if (FTA_STATUSES.has(leadStatus)) {
                    triggerAttendFailedClick(leadId, reminder);
                } else if (leadStatus === 'CONSULTATION_ATTENDED' || leadStatus === 'TREATMENT_STARTED') {
                    triggerAttendedPopup(leadId, reminder);
                } else {
                    triggerContact(leadId, reminder);
                }
            } else {
                postState(YES, { resolved_action: actionKey }).catch(() => {});
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Action failed — please try again.' }));
        }
    };

    return (
        <>
            <ChecklistCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon={actions[0].icon}
                nextActions={actions}
                onSelectNextAction={(key) => fireAction(key, null)}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
            {!onSelectedRow && dialogs}
        </>
    );
}
LeadNextActionCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onRefresh: PropTypes.func, onSelectedRow: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

function FreeTextCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const { loading, postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    const state = getChecklistItemCompletionState(cellData);

    const openPopover = useCallback(() => {
        setAnchorEl(document.activeElement);
    }, []);

    const handleSave = async (text) => {
        if (!text.trim()) return;
        try {
            await postState(YES, { text: text.trim() });
            setAnchorEl(null);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to save — please try again.' }));
        }
    };


    const savedText = cellData.response_payload?.text || null;
    // Truncate at 40 chars; show full text on hover via Tooltip
    let displayLabel = null;
    if (state !== PENDING && savedText) {
        displayLabel = savedText.length > 40 ? `${savedText.slice(0, 40)}…` : savedText;
    }

    return (
        <>
            <Tooltip title={state !== PENDING && savedText && savedText.length > 40 ? savedText : ''} placement="top" enterDelay={400}>
                <span style={{ display: 'contents' }}>
                    <ChecklistCell
                        cellData={{ ...cellData, _display_label: displayLabel || cellData._display_label }}
                        practiceId={practiceId}
                        leadData={leadData}
                        onUpdate={onUpdate}
                        actionIcon="eva:edit-outline"
                        onActionClick={openPopover}
                        onChecklistComplete={onChecklistComplete}
                        onChecklistReopen={onChecklistReopen}
                    />
                </span>
            </Tooltip>
            <FreeTextPopover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                onSave={handleSave}
                loading={loading}
            />
        </>
    );
}
FreeTextCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

function MultipleChoiceCell({ cellData, colMeta, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const cellRef = useRef(null);
    const { loading, postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);
    // Options live in column metadata (template config), not in the per-lead cell record
    const options = colMeta?.action_config?.options || cellData.action_config?.options || [];
    const savedChoices = cellData.response_payload?.choices;
    // choices may be IDs (post-migration) or labels (legacy) — resolve to labels for display
    const optionLabelMap = Object.fromEntries(options.map((o) => [_optId(o), _optLabel(o)]));
    const choiceLabel = Array.isArray(savedChoices) && savedChoices.length > 0
        ? savedChoices.slice(0, 2).map((c) => optionLabelMap[c] || c).join(', ') + (savedChoices.length > 2 ? ` +${savedChoices.length - 2}` : '')
        : null;

    const openPopover = useCallback(() => {
        if (options.length === 0) return;
        setAnchorEl(cellRef.current || document.activeElement);
    }, [options.length]);

    const handleSave = async (choices) => {
        try {
            await postState(YES, { choices });
            setAnchorEl(null);
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to save — please try again.' }));
        }
    };


    return (
        <>
            <div ref={cellRef} style={{ width: '100%', height: '100%' }}>
                <ChecklistCell
                    cellData={{ ...cellData, _display_label: choiceLabel || cellData._display_label }}
                    practiceId={practiceId}
                    leadData={leadData}
                    onUpdate={onUpdate}
                    actionIcon="eva:checkmark-square-outline"
                    onActionClick={openPopover}
                    onChecklistComplete={onChecklistComplete}
                    onChecklistReopen={onChecklistReopen}
                />
            </div>
            <MultipleChoicePopover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                options={options}
                onSave={handleSave}
                loading={loading}
            />
        </>
    );
}
MultipleChoiceCell.propTypes = { cellData: PropTypes.object.isRequired, colMeta: PropTypes.object, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// PaymentMethodCell — records payment taken for a lead treatment.
// Supersedes Wave 4-B response_payload-only approach: now writes through to
// LeadTreatmentPayment via POST /treatments/{tid}/payments/ so the pick
// appears on the lead's Payment & refunds tab.
// -----------------------------------------------

// Payment TYPE options — the simple set matching the Treatment Started
// "Payment method" column (raw payment_type values whose first letter renders
// as the chip avatar: D/C/T/F). No "how received", no payment link — by design.
const PAYMENT_TYPE_OPTIONS = [
    { value: 'DEPOSIT', label: 'Deposit' },
    { value: 'CONSULTATION', label: 'Consultation fee' },
    { value: 'TREATMENT', label: 'Treatment fee' },
    { value: 'FINANCE', label: 'Finance' },
    { value: 'TREATMENT_DEPOSIT', label: 'Treatment deposit' },
];

// Letter avatar + full title for a recorded payment, mirroring
// CustomDepositPaymentCell (the Treatment Started column).
const paymentChar = (p) => (
    p?.payment_processor?.payment_processor ? getCharAtName(p.payment_processor.payment_processor) : (getCharAtName(p?.payment_type) || '')
);
const paymentTitle = (p) => (
    p?.payment_processor?.payment_processor ? generateLabel(p.payment_processor.payment_processor) : generateLabel(p?.payment_type || 'Payment')
);

// PaymentMethodCell — records payments for a lead treatment, matching the
// Treatment Started "Payment method" column exactly: letter-avatar + £amount
// chips, a simple type + amount form.
//
// STATE-AWARE (fix, 2026-08): this cell used to receive only `leadData` — no
// `cellData` — so it could not render the checklist item's state at all. A step
// completed anywhere else (bulk bar, lead panel) still drew a bare "+", which read
// as "nothing recorded" and left staff pressing Complete repeatedly. It now renders
// the same state signals as every other column (tick / slash / tint / completed-at)
// alongside the payment chips, and carries the standard StatusMenu.
//
// Recording the FIRST payment completes the step as YES with
// response_payload {payment_id, payment_type, amount} — the exact keys
// apps/tasks/payload_schemas.py already whitelists for PAYMENT_METHOD. Later
// payments stay append-only (chips grow, state untouched); Reopen/Skip come from
// the StatusMenu.
function PaymentMethodCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [amountError, setAmountError] = useState(false);
    const [paymentType, setPaymentType] = useState('DEPOSIT');
    const [amount, setAmount] = useState('');
    const [menuAnchor, setMenuAnchor] = useState(null);
    const isMobile = useResponsive('down', 'sm');

    // Checklist state + the shared completion API (same hook ChecklistCell uses, so
    // counters, the TASKS_V2_CHANGED broadcast and the row exit animation all fire
    // exactly as they do for every other cell type).
    const state = getChecklistItemCompletionState(cellData);
    const itemType = cellData?.item_type || CHECKLIST_ITEM_TYPE.PAYMENT_METHOD;
    const isArchived = leadData?.is_open_for_communication === false;
    const { postState } = useStatusApi(practiceId, cellData, onUpdate, leadData, onChecklistComplete, onChecklistReopen);

    const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));
    const treatmentId = leadData?.treatment_id || leadData?.lead_treatment?.id || leadData?.originalData?.lead_treatment?.id;

    // Recorded payments come straight off the row (same field the column reads),
    // so the cell shows the right per-lead payments without a per-row fetch.
    // `localPayments` lets a freshly-added payment appear instantly before the
    // grid reload (onUpdate) lands; once the row's deposit_payment_data updates
    // (see effect below) we drop back to that canonical list.
    const rowPayments = Array.isArray(leadData?.deposit_payment_data) ? leadData.deposit_payment_data : [];
    const [localPayments, setLocalPayments] = useState(null);
    const payments = localPayments || rowPayments;

    // When the grid reloads the row after an add, the canonical deposit_payment_data
    // changes identity — re-sync to it (clears stale optimistic chips so the
    // dialog list and table reflect the server records, incl. processor info).
    const rowPaymentsRef = leadData?.deposit_payment_data;
    useEffect(() => { setLocalPayments(null); }, [rowPaymentsRef]);

    const handleOpen = () => {
        setLocalPayments(null);
        setPaymentType('DEPOSIT');
        setAmount('');
        setAmountError(false);
        setOpen(true);
    };

    if (!treatmentId) {
        return <DisabledCell tooltip="No treatment to record payment against — pick a treatment on the lead first" />;
    }

    // Append a NEW payment (always create — never edit/replace), refresh the
    // list, reset the amount. The dialog stays open so multiple payments can be
    // recorded in one go. The FIRST payment also completes the checklist step.
    const handleAddPayment = () => {
        if (saving) return; // in-flight guard — no double-submit
        const value = parseFloat(amount);
        if (!value || value <= 0) { setAmountError(true); return; }
        setSaving(true);
        dispatch(createTreatmentPayment({
            body: { payment_type: paymentType, amount: value },
            treatmentId,
            leadId,
            handleSuccess: (created) => {
                // Optimistic: show the new chip immediately (stable unique id so
                // repeated adds never collide on React keys).
                setLocalPayments((prev) => ([...(prev || rowPayments), { id: created?.id ?? `tmp-${Date.now()}-${value}`, payment_type: paymentType, amount: value }]));
                setAmount('');
                setAmountError(false);
                setSaving(false);
                // Money recorded ⇒ the step is satisfied. Only from a non-terminal
                // state: a second payment must not re-stamp completed_at or re-fire
                // the completion cascade, and must never resurrect a Skipped step
                // (use Reopen from the StatusMenu for that, deliberately).
                if (state === PENDING || state === PAUSED) {
                    const payload = { payment_type: paymentType, amount: value };
                    if (created?.id) payload.payment_id = created.id;
                    // postState refreshes the row itself (and fires counters +
                    // TASKS_V2_CHANGED), so don't also call onUpdate — that would
                    // double-fetch the grid on every first payment.
                    postState(YES, payload);
                    return;
                }
                if (onUpdate) onUpdate(); // reload row so deposit_payment_data refreshes
            },
            handleError: () => setSaving(false), // release the button on failure
        }));
    };

    // State signals, from the SAME helpers every other cell uses so this column can't
    // drift from the rest of the table: green tick on YES, slash on SKIPPED, the
    // matching tint, and the completed-at / paused-until label.
    const stateIcon = getCellIcon(state, 'material-symbols:add-circle');
    const cellColor = getCellColor(state, null, false);
    const dateLabel = formatCellDate(state, null, cellData?.completed_at, cellData?.paused_until);
    const pendingIconColor = payments.length ? 'rgba(145, 158, 171, 0.9)' : 'rgba(145, 158, 171, 0.6)';
    const iconColor = state === PENDING ? pendingIconColor : cellColor;
    const stateTooltip = getCellTooltip(state, null, cellData || {});
    // Method recorded via the lead-panel picker (mode_of_payment), shown as its own chip.
    const recordedMethod = cellData?.response_payload?.mode_of_payment || '';
    const addHint = payments.length ? 'View / add payments' : 'Record a payment';
    // eslint-disable-next-line no-nested-ternary
    const cellTooltip = isArchived
        ? 'Lead is archived'
        : (state === PENDING ? addHint : `${stateTooltip} — click to view / add payments`);

    return (
        <>
            <Tooltip title={cellTooltip}>
                <Box
                    onClick={(e) => { e.stopPropagation(); handleOpen(); }}
                    // Right-click / long-press opens the standard status menu, so Mark
                    // done / Skip / Reopen work here exactly as on every other column.
                    // Previously this cell had no state affordance at all.
                    onContextMenu={(e) => { e.preventDefault(); if (!isArchived) setMenuAnchor(e.currentTarget); }}
                    sx={{
                        ...tableCellInnerBoxStyle,
                        bgcolor: getCellBgTint(state, null),
                        display: 'flex', alignItems: 'center',
                        justifyContent: payments.length ? 'flex-start' : 'center',
                        flexWrap: 'wrap', gap: '4px', p: '6px 8px', cursor: 'pointer',
                        transition: 'background-color 150ms ease',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                >
                    {/* Status icon FIRST, then the money — "done, and here's what was
                        taken" in one glance. On a pending step the icon is the familiar
                        "+" so recording a payment stays the obvious action. */}
                    <Iconify
                        icon={stateIcon}
                        width={18}
                        sx={{ color: iconColor, opacity: state === SKIPPED ? 0.5 : 1, flexShrink: 0 }}
                    />
                    {/* The METHOD (In full / GoCardless / Finance / PAYG) when it was set on
                        the lead's checklist — that's what this column's header asks for, and
                        without this it was recorded but invisible here. Read from
                        mode_of_payment only: the ledger's payment_type is already on the £
                        chips, so falling back to it would just duplicate them. */}
                    {recordedMethod && (
                        <Tooltip title="Payment method recorded on the checklist" placement="bottom">
                            <Box sx={{ ...paymentChipStyle, fontWeight: 600 }}>{recordedMethod}</Box>
                        </Tooltip>
                    )}
                    {payments.map((p, i) => (
                        <Tooltip key={p.id ?? i} title={paymentTitle(p)} placement="bottom">
                            <Box sx={paymentChipStyle}>
                                <Box sx={paymentChipTypeStyle}>{paymentChar(p)}</Box>
                                {`£ ${p?.amount ?? 0}`}
                            </Box>
                        </Tooltip>
                    ))}
                    {dateLabel && (
                        <Typography
                            sx={{
                                fontFamily: MAC_FONT,
                                fontSize: '10px',
                                fontWeight: 500,
                                color: cellColor,
                                whiteSpace: 'nowrap',
                                textDecoration: state === SKIPPED ? 'line-through' : 'none',
                            }}
                        >
                            {dateLabel}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
            <StatusMenu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onSelect={(s) => { setMenuAnchor(null); if (!isArchived) postState(s); }}
                currentState={state}
                itemType={itemType}
                // This cell posts the chosen state straight to /complete/ (no pause dialog),
                // so Pause is not offered — it would need a paused_until we cannot collect
                // here. "Record the payment" is also not a step you defer to a date.
                allowPause={false}
            />
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Payments</DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    {/* Recorded payments so far — letter avatar + amount, like the column. */}
                    {payments.length > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {payments.map((p, i) => (
                                <Tooltip key={p.id ?? i} title={paymentTitle(p)} placement="bottom">
                                    <Box sx={paymentChipStyle}>
                                        <Box sx={paymentChipTypeStyle}>{paymentChar(p)}</Box>
                                        {`£ ${p?.amount ?? 0}`}
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                    )}
                    {/* Simple: pick a payment type, enter an amount. */}
                    <TextField
                        select fullWidth size="small" label="Payment type" value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value)} sx={{ mb: 2 }}
                    >
                        {PAYMENT_TYPE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth size="small" label="Amount" type="number" value={amount}
                        onChange={(e) => { setAmount(e.target.value); if (amountError) setAmountError(false); }}
                        error={amountError} helperText={amountError ? 'Enter an amount greater than 0' : ' '}
                        InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
                        Done
                    </Button>
                    <Button
                        onClick={handleAddPayment}
                        variant="contained"
                        disabled={saving}
                        sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                    >
                        {saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Add payment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
PaymentMethodCell.propTypes = {
    cellData: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadData: PropTypes.object,
    onUpdate: PropTypes.func,
    onChecklistComplete: PropTypes.func,
    onChecklistReopen: PropTypes.func,
};

// -----------------------------------------------
// StaffPickerCell — STAFF_PICKER checklist items (e.g. "Confirmed Aligners Order By").
// Saves the chosen staff member ON THE CHECKLIST ITEM — POST .../complete/ with
// response_payload.staff_user_id — exactly like the lead overview's
// SimpleFieldPanel, and shows that saved value. It must NEVER touch the lead's
// assignees: the previous version reused the lead assignee picker, so a pick
// re-assigned the lead, the cell then displayed the lead assignee (looking
// "filled"), while the checklist item stayed blank/pending — the overview read
// the empty checklist value and the task stayed overdue (2026-10-19).
// Status menu (skip / pause / reopen) comes from ChecklistCell like every cell.
// -----------------------------------------------
function StaffPickerCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [complete, setComplete] = useState(null); // ChecklistCell's completer (payload) => Promise
    const [saving, setSaving] = useState(false);
    const isMobile = useResponsive('down', 'sm');

    // Same staff source + security filter as the overview picker (FilterAssigneeList
    // drops ADMIN / SUPPORT / superusers). `id` is the user id the payload stores.
    const rawUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);
    const staff = useMemo(() => FilterAssigneeList(rawUsers), [rawUsers]);

    const savedId = readChecklistItemValue(CHECKLIST_ITEM_TYPE.STAFF_PICKER, cellData);
    const savedName = savedId
        ? (staff.find((m) => String(m.id) === String(savedId))?.name || 'Staff member')
        : '';

    const handlePick = async (member) => {
        if (!complete || saving) return;
        setSaving(true);
        try {
            // String id — matches what the overview (SimpleFieldPanel) writes.
            await complete({ staff_user_id: String(member.id) });
            setComplete(null);
        } catch {
            // ChecklistCell already rolled back its optimistic tick and useStatusApi
            // surfaced the error; keep the picker open so the user can retry.
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ChecklistCell
                cellData={savedName ? { ...cellData, _display_label: savedName } : cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon="eva:person-outline"
                onActionClick={(completer) => setComplete(() => completer)}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
            <Dialog
                open={Boolean(complete)}
                onClose={() => { if (!saving) setComplete(null); }}
                maxWidth="xs"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>{cellData?.title || 'Select staff member'}</DialogTitle>
                <DialogContent sx={{ px: 1, pb: 1 }}>
                    {staff.length === 0 ? (
                        <Typography sx={{ px: 2, py: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                            No staff members available for this practice.
                        </Typography>
                    ) : (
                        staff.map((member) => (
                            <MenuItem
                                key={member.id}
                                data-testid="staff-picker-option"
                                selected={String(member.id) === String(savedId)}
                                disabled={saving}
                                onClick={() => handlePick(member)}
                                sx={{ gap: 1.5, borderRadius: 1, py: 1 }}
                            >
                                <AssignUserAvatar sx={{ width: 26, height: 26, fontSize: '12px' }} src={member.avatar} name={member.name} role={member.role} />
                                <Typography sx={{ fontSize: '0.875rem' }}>{member.name}</Typography>
                                {saving && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
                            </MenuItem>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setComplete(null)} disabled={saving}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
StaffPickerCell.propTypes = {
    cellData: PropTypes.object.isRequired,
    practiceId: PropTypes.any,
    leadData: PropTypes.object,
    onUpdate: PropTypes.func,
    onChecklistComplete: PropTypes.func,
    onChecklistReopen: PropTypes.func,
};

// -----------------------------------------------
// DateFieldCell — opens existing reminder/task dialog with title pre-filled.
// Intentionally does NOT mark the checklist item YES — the user does that
// separately once the date-related action is complete.
// -----------------------------------------------
function DateFieldCell({ cellData, colMeta, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const defaultTitle = colMeta?.title || cellData?.title || colMeta?.column_label || cellData?.column_label || '';

    return (
        <>
            <ChecklistCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon="eva:calendar-outline"
                onActionClick={() => setOpen(true)}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
            <AddReminderPopup
                open={open}
                onClose={() => setOpen(false)}
                leadDetail={leadData}
                type="addTask"
                reload
                reloadTaskCounter
                page="LeadDetail"
                defaultTitle={defaultTitle}
            />
        </>
    );
}
DateFieldCell.propTypes = { cellData: PropTypes.object.isRequired, colMeta: PropTypes.object, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// SimpleDateCell — minimal date-ONLY field for item_type=SIMPLE_DATE.
// Unlike DateFieldCell (which opens the full AddReminderPopup with a
// DateTimePicker + reminder options), this is just a bare date picker: pick a
// date, it shows in the cell, done. No time, no reminder dialog, no extras.
// Persists the chosen date to the checklist item's response_payload.date via
// the complete endpoint (the same path DATE_FIELD uses; a plain PATCH value is
// silently dropped by the BE — see configs/DATE_FIELD.js notes).
// -----------------------------------------------
function SimpleDateCell({ cellData, practiceId, leadData, onUpdate, onChecklistComplete, onChecklistReopen }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const taskId = cellData?.task_id;
    const itemId = cellData?.checklist_item_id;
    const saved = cellData?.response_payload?.date || cellData?.value || null;

    // Setting a date completes the item (response_payload.date — same path
    // DATE_FIELD uses; a plain PATCH value is silently dropped by the BE).
    const handleAccept = async (newDate) => {
        setOpen(false);
        if (!newDate || !newDate.isValid() || !taskId || !itemId) return;
        const iso = newDate.format('YYYY-MM-DD');
        setSaving(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'post',
                { completion_state: YES, response_payload: { date: iso } }
            );
            if (res?.status >= 200 && res?.status < 300) {
                if (onChecklistComplete) onChecklistComplete();
                if (onUpdate) onUpdate();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {/* ChecklistCell gives the SAME options as every other action item —
                Skip / Pause / complete-state menu / reopen. The primary action
                (calendar icon) opens a date-ONLY modal picker instead of the
                heavy AddReminderPopup that DATE_FIELD uses. */}
            <ChecklistCell
                cellData={cellData}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
                actionIcon="eva:calendar-outline"
                onActionClick={() => setOpen(true)}
                onChecklistComplete={onChecklistComplete}
                onChecklistReopen={onChecklistReopen}
            />
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileDatePicker
                    open={open}
                    onClose={() => setOpen(false)}
                    onAccept={handleAccept}
                    value={saved ? moment(saved) : null}
                    views={['year', 'month', 'day']}
                    format="DD/MM/YYYY"
                    disabled={saving}
                    slotProps={{ textField: { sx: { display: 'none' } } }}
                />
            </LocalizationProvider>
        </>
    );
}
SimpleDateCell.propTypes = { cellData: PropTypes.object.isRequired, practiceId: PropTypes.any, leadData: PropTypes.object, onUpdate: PropTypes.func, onChecklistComplete: PropTypes.func, onChecklistReopen: PropTypes.func };

// -----------------------------------------------
// DisabledCell — shown when mode is TRIGGER_ONLY or MANUAL and no task exists yet
// Renders the same icon+date one-row layout as active cells, just fully disabled,
// so the column reads visually consistent regardless of whether a task instance exists.
// -----------------------------------------------
function DisabledCell({ tooltip = '', scheduledDate, actionIcon }) {
    const dateLabel = scheduledDate ? formatCellDate(PENDING, scheduledDate, null, null) : '';
    return (
        <Tooltip title={tooltip}>
            <Box
                sx={{
                    ...tableCellInnerBoxStyle,
                    bgcolor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    p: '8px 10px',
                    opacity: 0.45,
                    pointerEvents: 'none',
                }}
            >
                {actionIcon && (
                    <Iconify icon={actionIcon} width={16} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                )}
                {dateLabel && (
                    <Typography sx={{ fontFamily: MAC_FONT, fontSize: '10px', fontWeight: 500, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                        {dateLabel}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
}
DisabledCell.propTypes = { tooltip: PropTypes.string, scheduledDate: PropTypes.string, actionIcon: PropTypes.string };

// -----------------------------------------------
// PendingActionCell — ON_CLICK mode: first click creates the task
// -----------------------------------------------
function PendingActionCell({ colMeta, leadId, practiceId, leadData, onUpdate }) {
    const [creating, setCreating] = useState(false);
    const [createdCellData, setCreatedCellData] = useState(null);
    const [composerOpen, setComposerOpen] = useState(false);
    const [composerType, setComposerType] = useState('EMAIL');
    // Tracks the brief window right after a successful instantiation when a
    // second click would re-POST /from-template/ and create a duplicate Task.
    // Cleared 1.5s after instantiation (or when parent refetch lands and the
    // PendingActionCell is replaced by the real cell, whichever first).
    const justInstantiatedRef = useRef(false);
    useEffect(() => () => { justInstantiatedRef.current = false; }, []);

    // ETM: resolve the per-practice email template for this just-instantiated
    // cell (source item id = colMeta.id), preload it into the composer.
    const resolvedEmailTemplateId = useResolvedEmailTemplateId(composerOpen, colMeta?.item_type, colMeta?.id, leadData, practiceId);
    const effectiveEmailTemplateId = resolvedEmailTemplateId ?? createdCellData?.action_config?.template_id ?? null;

    const threadMessages = useSelector((state) => state.whatsappV2.threadMessages);
    const waThreadInfo = useMemo(() => {
        if (!Array.isArray(threadMessages) || !leadData?.id) return null;
        const numericLeadId = String(leadData?.lead_id ?? String(leadData?.id ?? '').split('-')[0]);
        const contact = threadMessages.find((c) => String(c?.lead?.id) === numericLeadId);
        if (!contact) return null;
        return { thread: contact, phoneNumber: contact?.to_waid || '' };
    }, [threadMessages, leadData?.id]);

    const itemType = colMeta?.item_type || CHECKLIST_ITEM_TYPE.CONFIRMATION;

    const getActionIcon = () => {
        switch (itemType) {
            case CHECKLIST_ITEM_TYPE.EMAIL_SENT: return 'eva:email-outline';
            case CHECKLIST_ITEM_TYPE.WHATSAPP_SENT: return 'ri:whatsapp-fill';
            case CHECKLIST_ITEM_TYPE.SMS_SENT: return 'eva:message-square-outline';
            case CHECKLIST_ITEM_TYPE.PHONE_CALLED: return 'eva:phone-outline';
            case CHECKLIST_ITEM_TYPE.FREE_TEXT: return 'eva:edit-outline';
            case CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE: return 'eva:checkmark-square-outline';
            case CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL: return 'eva:phone-call-outline';
            case CHECKLIST_ITEM_TYPE.ADD_NOTE: return 'eva:file-text-outline';
            case CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM: return 'eva:file-text-outline';
            case CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST: return 'eva:star-outline';
            case CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK: return 'eva:credit-card-outline';
            case CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER: return 'eva:bell-outline';
            case CHECKLIST_ITEM_TYPE.OPEN_FOLLOW_UP: return 'eva:person-done-outline';
            case CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT: return 'eva:calendar-outline';
            case CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE: return 'akar-icons:check';
            case CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND: return 'ci:close-big';
            case CHECKLIST_ITEM_TYPE.START_TREATMENT: return 'eva:play-circle-outline';
            case CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT: return 'eva:checkmark-circle-outline';
            case CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN: return 'eva:file-text-outline';
            case CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT: return 'eva:upload-outline';
            case CHECKLIST_ITEM_TYPE.LINKED_CHECKLIST: return 'eva:link-outline';
            case CHECKLIST_ITEM_TYPE.LEAD_STATUS_CHANGED: return 'eva:swap-outline';
            case CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION: return 'solar:bolt-bold-duotone';
            case CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE: return 'eva:toggle-right-outline';
            case CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT: return 'eva:pricetags-outline';
            case CHECKLIST_ITEM_TYPE.PAYMENT_METHOD: return 'eva:credit-card-outline';
            case CHECKLIST_ITEM_TYPE.STAFF_PICKER: return 'eva:person-outline';
            case CHECKLIST_ITEM_TYPE.DATE_FIELD: return 'eva:calendar-outline';
            case CHECKLIST_ITEM_TYPE.SIMPLE_DATE: return 'eva:calendar-outline';
            default: return 'eva:plus-circle-outline';
        }
    };

    const handleClick = useCallback(async () => {
        if (creating) return;
        // Belt-and-braces against duplicate /from-template/ POSTs: once we've
        // instantiated, ignore clicks for 1.5s. The inline rendered cell
        // (below) already covers most of this window, but on slow paint or
        // overlapping click events the underlying Box can still receive a
        // second pointer event before the inline render replaces it.
        if (justInstantiatedRef.current) return;
        if (createdCellData) return;
        setCreating(true);
        try {
            const leadTreatmentId = leadData?.treatment_id || leadData?.originalData?.lead_treatment?.id || null;
            const fromTemplateBody = { template_id: colMeta.template_id, lead_id: leadId };
            if (leadTreatmentId) fromTemplateBody.lead_treatment_id = leadTreatmentId;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'POST',
                fromTemplateBody
            );
            if (response.status === 200 || response.status === 201) {
                const taskData = response.data || {};
                // BE returns child_checklist_items (flat array keyed by template_item_id)
                // because the parent Task itself holds no ChecklistItems — they live on
                // child Tasks. Match the clicked column to the right child item.
                const childItems = Array.isArray(taskData.child_checklist_items)
                    ? taskData.child_checklist_items
                    : [];
                const matchedItem = childItems.find(
                    (it) => String(it.template_item_id) === String(colMeta?.id)
                );
                if (!matchedItem) {
                    // BE just instantiated and should have created+linked this item.
                    // Missing match = server bug or stale colMeta — fail loud rather
                    // than silently 404 on the next /complete/ call.
                    dispatch(updateError({
                        success: false,
                        message: 'Failed to initialise cell — please refresh and try again.',
                    }));
                    if (onUpdate) onUpdate();
                    return;
                }
                const synthetic = {
                    task_id: matchedItem.task_id,
                    checklist_item_id: matchedItem.id,
                    item_type: matchedItem.item_type || itemType,
                    completion_state: matchedItem.completion_state || CHECKLIST_COMPLETION_STATE.PENDING,
                    action_config: colMeta?.action_config || {},
                    template_item_id: colMeta?.id,
                };
                setCreatedCellData(synthetic);
                justInstantiatedRef.current = true;
                setTimeout(() => { justInstantiatedRef.current = false; }, 1500);

                if (itemType === CHECKLIST_ITEM_TYPE.CONFIRMATION) {
                    onUpdate();
                } else if ([CHECKLIST_ITEM_TYPE.EMAIL_SENT, CHECKLIST_ITEM_TYPE.WHATSAPP_SENT, CHECKLIST_ITEM_TYPE.SMS_SENT].includes(itemType)) {
                    let channel = 'SMS';
                    if (itemType === CHECKLIST_ITEM_TYPE.EMAIL_SENT) channel = 'EMAIL';
                    else if (itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT) channel = 'WHATSAPP';
                    setComposerType(channel);
                    setComposerOpen(true);
                } else {
                    // Non-composer types (ADD_NOTE, SCHEDULE_REMINDER, SEND_PAYMENT_LINK,
                    // SEND_CONSENT_FORM, SEND_REVIEW_REQUEST, UPLOAD_DOCUMENT, etc.):
                    // request a refresh so the parent eventually replaces this
                    // PendingActionCell with the real routed cell, but ALSO keep
                    // `createdCellData` set so the inline render path below shows
                    // the instantiated cell immediately — no PendingActionCell
                    // flash, no duplicate-click window.
                    onUpdate();
                }
            } else {
                dispatch(updateError({ success: false, message: 'Failed to create task — please try again' }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to create task — please try again' }));
        } finally {
            setCreating(false);
        }
    }, [creating, practiceId, colMeta, leadId, itemType, onUpdate]);

    const handleComposerSend = () => {
        setComposerOpen(false);
        setCreatedCellData(null);
        onUpdate();
    };

    if (itemType === CHECKLIST_ITEM_TYPE.SMS_SENT) {
        return <DisabledCell tooltip="SMS sending is not enabled for this practice yet" />;
    }

    // Non-composer types: once we hold a synthetic cellData, render the real
    // cell inline so the user immediately sees the instantiated state and can
    // act on it. The TaskChecklistField router below routes by item_type and
    // is safe to re-enter — our synthetic includes task_id so the `pending`
    // branch (which renders PendingActionCell) is bypassed.
    const isComposerType = [
        CHECKLIST_ITEM_TYPE.EMAIL_SENT,
        CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
        CHECKLIST_ITEM_TYPE.SMS_SENT,
    ].includes(itemType);
    if (createdCellData && !isComposerType) {
        return (
            <TaskChecklistField
                cellData={createdCellData}
                colMeta={colMeta}
                practiceId={practiceId}
                leadData={leadData}
                onUpdate={onUpdate}
            />
        );
    }

    return (
        <>
            <Tooltip title="Click to create and start this task">
                <Box
                    sx={{
                        ...tableCellInnerBoxStyle,
                        cursor: creating ? 'default' : 'pointer',
                        opacity: creating ? 0.6 : 1,
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
                    }}
                    onClick={handleClick}
                >
                    {creating ? (
                        <CircularProgress size={18} />
                    ) : (
                        <IconButton size="small" sx={{ p: 0.5, color: 'primary.main', pointerEvents: 'none' }}>
                            <Iconify icon={getActionIcon()} width={18} />
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
            {composerOpen && createdCellData && leadData && composerType === 'WHATSAPP' && (
                <IosWhatsAppComposer
                    open={composerOpen}
                    onClose={() => { setComposerOpen(false); setCreatedCellData(null); onUpdate(); }}
                    practiceId={practiceId}
                    leadId={(leadData?.lead_id || String(leadData?.id ?? '').split('-')[0])}
                    leadDetail={leadData}
                    phoneNumber={waThreadInfo?.phoneNumber || leadData?.phone || leadData?.lead_contact?.phone || ''}
                    onSendSuccess={handleComposerSend}
                />
            )}
            {composerOpen && createdCellData && leadData && composerType === 'EMAIL' && (
                <IosEmailComposer
                    open={composerOpen}
                    onClose={() => { setComposerOpen(false); setCreatedCellData(null); onUpdate(); }}
                    practiceId={practiceId}
                    leadId={(leadData?.lead_id || String(leadData?.id ?? '').split('-')[0])}
                    leadDetail={leadData}
                    templateId={effectiveEmailTemplateId}
                    onSendSuccess={handleComposerSend}
                />
            )}
            {composerOpen && createdCellData && leadData && composerType === 'SMS' && (
                <LeadTemplateDialog
                    open={composerOpen}
                    onClose={() => { setComposerOpen(false); setCreatedCellData(null); onUpdate(); }}
                    onSend={handleComposerSend}
                    practiceId={practiceId}
                    type={composerType}
                    leadDetail={leadData}
                    templateId={createdCellData.action_config?.template_id}
                />
            )}
        </>
    );
}
PendingActionCell.propTypes = {
    colMeta: PropTypes.object.isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadData: PropTypes.object,
    onUpdate: PropTypes.func.isRequired,
};

// -----------------------------------------------
// LeadTemplateDialog — lazy-loaded LeadTemplate wrapper
// NOTE: EmailMessageCompose and FollowUpWhatsappChat are complex global-state
// components without clean onSend callbacks; LeadTemplate remains the correct
// abstraction. Flagged for product discussion if a dedicated lightweight
// per-cell composer is required in future.
// -----------------------------------------------
let _LeadTemplate = null;
function LeadTemplateDialog({ open, onClose, onSend = null, practiceId, type, leadDetail, templateId }) {
    const [LT, setLT] = useState(_LeadTemplate);
    const isMounted = useRef(true);

    useEffect(() => {
        if (!_LeadTemplate) {
            import('src/pages/dashboard/LeadTemplate')
                .then((mod) => {
                    _LeadTemplate = mod.default;
                    if (isMounted.current) setLT(() => mod.default);
                })
                .catch((err) => {
                    console.error('Failed to load LeadTemplate:', err);
                    if (isMounted.current) setLT(() => null);
                });
        }
        return () => { isMounted.current = false; };
    }, []);

    if (!LT || !open || !leadDetail) return null;

    return (
        <LT
            openEmailCompose={open}
            onCloseEmailCompose={onClose}
            onSendSuccess={onSend}
            practiceId={String(practiceId)}
            type={type}
            leadDetail={leadDetail}
            bindData={Boolean(templateId)}
        />
    );
}
LeadTemplateDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSend: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string.isRequired,
    leadDetail: PropTypes.object,
    templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// -----------------------------------------------
// Main router
// -----------------------------------------------
const TaskChecklistField = ({ cellData, colMeta, practiceId, leadData, onUpdate, onRefresh, onSelectedRow, onChecklistComplete, onChecklistReopen }) => {
    if (!cellData || !cellData.task_id) {
        const mode = colMeta?.instantiation_mode;
        const leadId = (leadData?.lead_id
        || leadData?.lead?.id
        || leadData?.lead_treatment?.lead
        || (typeof leadData?.id === 'number' ? leadData.id : String(leadData?.id ?? '').split('-')[0]));

        if (mode === INSTANTIATION_MODE.ON_CLICK && leadId && onUpdate) {
            return (
                <PendingActionCell
                    colMeta={colMeta}
                    leadId={leadId}
                    practiceId={practiceId}
                    leadData={leadData}
                    onUpdate={onUpdate}
                />
            );
        }

        let tooltip = '';
        if (mode === INSTANTIATION_MODE.MANUAL) {
            tooltip = 'Click Apply To Lead on this template to activate.';
        } else if (mode === INSTANTIATION_MODE.TRIGGER_ONLY) {
            tooltip = 'This will activate when the lead matches the trigger condition for this template.';
        // eslint-disable-next-line react/prop-types
        } else if (colMeta?.due_anchor === 'ATTENDED') {
            tooltip = 'Unlocks once the consultation is attended';
        }
        return <DisabledCell tooltip={tooltip} />;
    }

    // Role guard (#100) — BE sets is_role_allowed=false on columns the current user's role cannot fire.
    if (colMeta?.is_role_allowed === false) {
        return <DisabledCell tooltip="Requires elevated role (ADMIN, TCO, or USER)" />;
    }

    // Treatment resolver guard (#101) — BE sets has_resolvable_treatment=false when no active treatment
    // can be resolved for treatment-touching cell types (BOOK_APPOINTMENT, START_TREATMENT, etc.).
    if (cellData?.has_resolvable_treatment === false) {
        return <DisabledCell tooltip="No matching treatment for this action" />;
    }

    const itemType = cellData.item_type || colMeta?.item_type || CHECKLIST_ITEM_TYPE.CONFIRMATION;

    // ── CHECKLIST_ITEM_TYPE → cell handler audit (Wave 3, 2026-05-16) ──────────
    // Every value in CHECKLIST_ITEM_TYPE (src/constants/tasksV2.js) MUST appear
    // below with an explicit `case`. Adding to the enum without adding a `case`
    // here is a silent failure: the cell falls through to ConfirmationCell and
    // the user sees a generic checkbox instead of the intended UX.
    //
    //   CONFIRMATION             → ConfirmationCell
    //   EMAIL_SENT               → ComposerCell (EMAIL)
    //   WHATSAPP_SENT            → ComposerCell (WHATSAPP)
    //   SMS_SENT                 → DisabledCell ("SMS not enabled")
    //   PHONE_CALLED             → OpenFollowUpCell (phone icon) [deprecated]
    //   FREE_TEXT                → FreeTextCell
    //   MULTIPLE_CHOICE          → MultipleChoiceCell
    //   LOG_PHONE_CALL           → LogPhoneCallCell
    //   ADD_NOTE                 → AddNoteCell
    //   SEND_CONSENT_FORM        → SendConsentFormCell
    //   SEND_REVIEW_REQUEST      → SendReviewRequestCell
    //   SEND_PAYMENT_LINK        → SendPaymentLinkCell
    //   SCHEDULE_REMINDER        → ScheduleReminderCell
    //   OPEN_FOLLOW_UP           → OpenFollowUpCell [deprecated → LEAD_NEXT_ACTION]
    //   BOOK_APPOINTMENT         → BookAppointmentCell
    //   RECORD_ATTENDANCE        → RecordAttendanceCell [deprecated]
    //   RECORD_FAILED_TO_ATTEND  → RecordFailedToAttendCell [deprecated]
    //   START_TREATMENT          → StartTreatmentCell [deprecated]
    //   COMPLETE_TREATMENT       → CompleteTreatmentCell [deprecated]
    //   SEND_TREATMENT_PLAN      → SendTreatmentPlanCell
    //   UPLOAD_DOCUMENT          → UploadDocumentCell
    //   LINKED_CHECKLIST         → LinkedChecklistCell
    //   LEAD_STATUS_CHANGED      → LeadStatusChangedCell [deprecated]
    //   LEAD_NEXT_ACTION         → LeadNextActionCell
    //   YES_NO_TOGGLE            → YesNoToggleCell
    //   PAYMENT_METHOD           → PaymentMethodCell
    //   CURRENCY_AMOUNT          → FreeTextCell (routed; no dedicated currency input yet — TODO)
    //   STAFF_PICKER             → DisabledCell ("requires dedicated handler — coming soon")
    //   DATE_FIELD               → DateFieldCell (opens reminder dialog pre-filled with item title)
    //
    // `default:` retained as a defensive backstop only. If you find yourself
    // falling through, you've added an enum value without a case — fix it here.
    switch (itemType) {
        case CHECKLIST_ITEM_TYPE.EMAIL_SENT:
            return <ComposerCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} icon="eva:email-outline" composerType="EMAIL" onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} onSelectedRow={onSelectedRow} />;
        case CHECKLIST_ITEM_TYPE.WHATSAPP_SENT:
            return <ComposerCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} icon="ri:whatsapp-fill" composerType="WHATSAPP" onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} onSelectedRow={onSelectedRow} />;
        case CHECKLIST_ITEM_TYPE.SMS_SENT:
            return <DisabledCell tooltip="SMS sending is not enabled for this practice yet" />;
        case CHECKLIST_ITEM_TYPE.PHONE_CALLED:
            return <OpenFollowUpCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} actionIcon="eva:phone-outline" onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.FREE_TEXT:
            return <FreeTextCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE:
            return <MultipleChoiceCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL:
            return <LogPhoneCallCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onSelectedRow={onSelectedRow} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.ADD_NOTE:
            return <AddNoteCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM:
            return <SendConsentFormCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST:
            return <SendReviewRequestCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK:
            return <SendPaymentLinkCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER:
            return <ScheduleReminderCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.OPEN_FOLLOW_UP:
            return <OpenFollowUpCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT:
            return <BookAppointmentCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE:
            return <RecordAttendanceCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND:
            return <RecordFailedToAttendCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.START_TREATMENT:
            return <StartTreatmentCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT:
            return <CompleteTreatmentCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN:
            return <SendTreatmentPlanCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT:
            return <UploadDocumentCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.LINKED_CHECKLIST:
            return <LinkedChecklistCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.LEAD_STATUS_CHANGED:
            return <LeadStatusChangedCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION:
            return <LeadNextActionCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onRefresh={onRefresh} onSelectedRow={onSelectedRow} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE:
            return <YesNoToggleCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.PAYMENT_METHOD:
            // cellData is REQUIRED here: without it the cell cannot show the step's
            // state (the bug that made a completed/skipped payment step look untouched).
            return <PaymentMethodCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT:
            // Routed to FreeTextCell. Currency parsing/formatting lives in the
            // popover input today; a dedicated CurrencyAmountCell with a £-prefixed
            // numeric field is a follow-up. Until then, do NOT fall through to
            // ConfirmationCell — that masked the "amount typed but not saved" bug.
            return <FreeTextCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.STAFF_PICKER:
            // Saves the staff member on the CHECKLIST ITEM (response_payload.staff_user_id),
            // never on the lead's assignees — see StaffPickerCell.
            return <StaffPickerCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.DATE_FIELD:
            return <DateFieldCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.SIMPLE_DATE:
            return <SimpleDateCell cellData={cellData} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
        case CHECKLIST_ITEM_TYPE.CONFIRMATION:
        default:
            // Defensive backstop. If a new enum value lands without a case the
            // dev sees a Confirmation cell — which is the bug class this audit
            // exists to prevent. Add a case above instead of relying on this.
            return <ConfirmationCell cellData={cellData} colMeta={colMeta} practiceId={practiceId} leadData={leadData} onUpdate={onUpdate} onChecklistComplete={onChecklistComplete} onChecklistReopen={onChecklistReopen} />;
    }
};

// Wrap outer TaskChecklistField exports in BeforeReopenContext.Provider
const TaskChecklistFieldOuter = TaskChecklistField;
const TaskChecklistFieldWrapped = ({ onBeforeReopen, ...rest }) => (
    <BeforeReopenContext.Provider value={onBeforeReopen || null}>
        <TaskChecklistFieldOuter {...rest} />
    </BeforeReopenContext.Provider>
);
TaskChecklistFieldWrapped.propTypes = { onBeforeReopen: PropTypes.func };

TaskChecklistField.propTypes = {
    cellData: PropTypes.object,
    colMeta: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        template_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        instantiation_mode: PropTypes.string,
        item_type: PropTypes.string,
        action_config: PropTypes.object,
        is_role_allowed: PropTypes.bool,
    }),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadData: PropTypes.object,
    onUpdate: PropTypes.func,
    onRefresh: PropTypes.func,
    onSelectedRow: PropTypes.func,
    onChecklistComplete: PropTypes.func,
    onChecklistReopen: PropTypes.func,
};

export default TaskChecklistFieldWrapped;
